/**
 * B31 — the storefront container: wires context (§9.2 distributions),
 * session (token-based, no cookies), the contract-pinned API client, the
 * B20b(h) guest gate, and the §7 flow reducer into screens. Effects live
 * here; everything it composes is pure and separately tested.
 *
 * B20b rebased — the stage limits the first slices surfaced honestly are
 * now wired for real: buyers mint a guest session before the first read,
 * the catalog renders B20b(a) cards, the cart is server-authoritative via
 * cart.get, checkout carries the buyer email to ensureGuestCustomer, and
 * payment mounts Stripe elements over intent.create's clientSecret +
 * publishableKey. Fail-closed walls remain walls: a stage without guest
 * keys or channel config names the gap instead of half-working.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { AppCanvas, Button, FlowStepper, Icon, SheetFoot, StickyBar, ThemeToggle, isTenantType, vocabularyFor, type TenantType } from "@transact/ui";
import { ApiError, ApiRequestError, createApiClient, type ApiClient, type CallOptions } from "./lib/api.js";
import { runWithCartHeal } from "./lib/cartHeal.js";
import { createBearerHealingClient } from "./lib/bearerHeal.js";
import { resolveContext, type StorefrontContext } from "./lib/context.js";
import { embedChrome, funnelSurfaceOf } from "./lib/presentation.js";
import { bootFace, classifyBoot, mergeResolvedChannel, needsSiteKeyResolve, resolveSiteKey } from "./lib/boot.js";
/* B332 — a refusal is about ONE step of the walk; the module decides where it speaks. */
import { noticeOnScreen, noticeReferenceOnScreen, type BuyerNotice } from "./lib/notice.js";
import { formatMoney } from "./lib/money.js";
import { deskRepliesLead, deskRepliesWords, groupParty, inquiryBody, inquiryReady, inquirySendRefusal, reduceToKeep, seedInquiryDraft, type GroupInquiryDraft, type InquiryRefusal } from "./lib/groupBooking.js";
import { GROUP_INQUIRY_FORM_ID, GroupInquiryForm, GroupInquirySent, inquiryGateSentence } from "./ui/groupBooking.js";
import { recallGroupThreshold, recallGroupsContactName, rememberGroupThreshold, rememberGroupsContactName } from "./lib/theme.js";
import { ORDER_POLL_INTERVAL_MS, shouldPollOrder } from "./lib/orderPoll.js";
import { createSession } from "./lib/session.js";
import { createGuestGate } from "./lib/guest.js";
import { createFunnelReporter } from "./lib/funnel.js";
import {
  cartDeltas,
  contextFor,
  createTracker,
  installGtm,
  gtmAmount,
  hostedSink,
  lineItems,
  linesValue,
  listItems,
  productItem,
  productValue,
  relaySink,
  type LineLike,
  type Tracker,
} from "./lib/analytics.js";
import { applyChannelTheme, brandSheetFor, createThemeSink, recallBusinessName, recallChannelTheme, recallPublishableKey, recallStripeAccount, forgetTenantType,
  recallTenantType, recallGtmContainerId, rememberGtmContainerId, rememberBusinessName, rememberVenue, recallVenue, rememberChannelTheme, rememberPublishableKey, rememberStripeAccount, rememberTenantType,
  forgetClock, recallClock, rememberClock, type ClockFacts, type ChannelThemeLike, type ThemeSink } from "./lib/theme.js";
import {
  createDeferredPaymentGateway,
  loadStripeFactory,
  type DeferredPaymentGateway,
  type ExpressHandlers,
  type PayMint,
} from "./lib/gateway.js";
import { documentPaymentAppearance, observePaymentAppearance } from "./lib/paymentAppearance.js";
import {
  connectBridge,
  hostErrors,
  mergeInit,
  themeToCssVars,
  type Bridge,
  type BridgeWindowLike,
} from "./lib/bridge.js";
import { createDeparturesOpener, loadAvailability, loadCalendarPricing, loadDepartures, loadFitDepartures, needsDepartureStep } from "./lib/browse.js";
import {
  addPlan,
  emptyComposition,
  flowOrderOf,
  setQuantity,
  totalQuantity as compositionTotal,
  cartIssueHints,
  type Composition,
} from "./lib/composition.js";
import { initialFlow, reduceFlow } from "./lib/flow.js";
import { holdDeadline, holdMsLeft } from "./lib/hold.js";
import {
  ACKNOWLEDGE_REFUSAL,
  acknowledgementFor,
  optInFor,
  buyerFieldOf,
  buyerFieldProblems,
  buyerFieldSentence,
  buyerSetCall,
  checkoutBody,
  NO_BUYER,
} from "./lib/buyerAttach.js";
import { resumeDecision } from "./lib/resume.js";
import { mergeAddPlan } from "./lib/cartMerge.js";
import { confirmationSummary, departureLabel, slotDisplay } from "./lib/confirmation.js";
import { ticketQrDataUrl } from "./lib/qr.js";
import { promoAttempt, promoRefusedWhy } from "./lib/promo.js";
import {
  journeyAddPlan,
  journeyDepartures,
  journeyArrival,
  journeyOf,
  returnCardFor,
  cardPriceFacts,
  routeGroups,
  sameDayKey,
} from "./lib/journey.js";
import { TripTypeChoice } from "./ui/journey.js";
import { PAGE_MISCONFIGURED, UNKNOWN_FAILURE, translateRefusal } from "./lib/refusals.js";
import type { Money } from "./lib/money.js";
import { admitsCartAct, cartActKey, type CartAct } from "./lib/busyFace.js";
import { accompanimentNote, catalogSummary, nextDepartureFact, type BuyerVocabulary, returnPickLabel } from "./lib/bookingFace.js";
import { DeparturePicker, groupDepartures, type CalendarPricingWire } from "./ui/departures.js";
import { displayTotal, TicketsStep } from "./ui/ticketsStep.js";
import { mediaFromProductRead, type ProductMedia } from "./lib/productPage.js";
import { INSIDER_FAILED, insiderEmailWhy, insiderFor, insiderJoinCall } from "./lib/insider.js";
import { InsiderBand } from "./ui/insider.js";
import { footFacts, type VenueFacts } from "./lib/footFacts.js";
import { CartPayView } from "./ui/cartPay.js";
import { DialogShell, type DialogStep } from "./ui/dialogShell.js";
import { WidgetView, widgetPayWall } from "./ui/widget.js";
import { cartAvailabilityHints, cartCardLines, chargeRows, type ChargeRow } from "./lib/cartPayFace.js";
import {
  BookingHead,
  BookingHero,
  Busy,
  ShellFoot,
  CompositionView,
  ConfirmationView,
  ExpiredView,
  NotConnectedView,
  Notice,
  ProductList,
  Stepper,
  pickScreen,
  type CartAdjustment,
  type CartLine,
  type EntitlementSummary,
  type ScreenKind,
  type TicketView,
  type PoolAvailability,
  type ProductSummary,
  type SlotSummary,
  TicketsPageHead,
} from "./ui/screens.js";
import { PosterWall, type PosterFeed, type ShowView } from "./ui/posterWall.js";
import { ShowTicketsStep } from "./ui/showTickets.js";
import { cartShowHeads } from "./lib/showTickets.js";
import { thisMonthKey } from "./lib/showCalendar.js";
import { posterNights, POSTER_WINDOW_DAYS } from "./lib/posterWall.js";
/** B629 (c) — the charge fields the cart's totals and the confirmed order's detail share. */
type ChargeWire = Parameters<typeof chargeRows>[0];

interface AppConfig {
  readonly apiBaseUrl: string;
  readonly tenantId: string;
  readonly channelId: string;
  readonly currency: string;
}

function readConfig(ctx: StorefrontContext): AppConfig {
  const env = import.meta.env as Record<string, string | undefined>;
  return {
    apiBaseUrl: env["VITE_API_BASE_URL"] ?? "",
    tenantId: ctx.tenantId ?? env["VITE_TENANT_ID"] ?? "",
    channelId: ctx.channelId ?? "web",
    currency: env["VITE_CURRENCY"] ?? "USD",
  };
}

export default function App(): JSX.Element {
  const [ctx, setCtx] = useState<StorefrontContext>(() =>
    resolveContext({ href: window.location.href, framed: window.self !== window.top }),
  );
  /* B523 — the dialog presentation walks the STEPPED sheet (Tickets →
     Day & time → Cart → Pay → Done) and never the fused widget card. */
  const dialogWalk = ctx.presentation === "dialog";

  /* B52(d) — one sink, two writers: host init hints own their vars, the
     channel theme (riding the session.guest mint) fills the rest — in both
     arrival orders, because the bridge init and the mint race. */
  const themeSink = useMemo<ThemeSink>(() => createThemeSink(document.documentElement.style, document.documentElement, brandSheetFor(document)), []);
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | undefined>(undefined);
  /* B403 #26 (a) — the channel's hand-written headline (75:2), from the
     mint's theme; the catalog-derived summary stays the fallback. */
  const [headline, setHeadline] = useState<string | undefined>(undefined);
  /* B403 #26 (c)/(g) — the tenant's name, from the mint; recalled at boot
     like the theme so a live-token reload still paints it. */
  /* B524 leg 2 — the tenant's GTM container (hosted pages only; see installGtm). */
  const [hostedContainerId, setHostedContainerId] = useState<string | undefined>(() => {
    try {
      return recallGtmContainerId(window.sessionStorage);
    } catch {
      return undefined;
    }
  });
  const [businessName, setBusinessName] = useState<string | undefined>(() => {
    try {
      return recallBusinessName(window.sessionStorage);
    } catch {
      return undefined;
    }
  });
  /* B653 (c) — where guests find the venue: the same mint, the same recall-at-boot seam. */
  const [venue, setVenue] = useState<VenueFacts>(() => {
    try {
      return recallVenue(window.sessionStorage);
    } catch {
      return {};
    }
  });
  /* B403 #22-extension — the tenant's TYPE, same mint, same recall-at-boot
     seam: the buyer sentences speak the tenant's own vocabulary pack. */
  const [tenantType, setTenantType] = useState<TenantType | undefined>(() => {
    try {
      const recalled = recallTenantType(window.sessionStorage);
      return recalled !== undefined && isTenantType(recalled) ? recalled : undefined;
    } catch {
      return undefined;
    }
  });
  /* B621 — the tenant's clock facts (timezone + 12h/24h), same mint, same
     recall-at-boot seam: every buyer time speaks the TENANT's zone and
     chosen face, never the viewer's. */
  const [clock, setClock] = useState<ClockFacts | undefined>(() => {
    try {
      return recallClock(window.sessionStorage);
    } catch {
      return undefined;
    }
  });
  const whenOpts = useMemo(
    () => ({
      ...(clock?.timezone !== undefined ? { timeZone: clock.timezone } : {}),
      ...(clock?.timeFormat !== undefined ? { timeFormat: clock.timeFormat } : {}),
    }),
    [clock],
  );

  /* B31a — the §9.2 bridge: embedded mode handshakes with the host page. */
  const [bridge, setBridge] = useState<Bridge | undefined>(undefined);
  /* B517 — bumps when an init pins the host: the first resize is posted
     before the handshake completes (and dropped), so the pin re-sends it. */
  const [pinnedCount, setPinnedCount] = useState(0);
  useEffect(() => {
    if (ctx.distribution !== "embedded") return;
    const b = connectBridge(window as unknown as BridgeWindowLike, (init) => {
      setPinnedCount((n) => n + 1);
      setCtx((prev) => mergeInit(prev, init));
      themeSink.applyHostVars(themeToCssVars(init.theme));
    }, hostErrors);
    setBridge(b);
    return () => {
      b.disconnect();
      setBridge(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- distribution is fixed at load
  }, []);

  /* B31a — auto-resize: the host page sizes the iframe to our content.
     B545 (g): NOT in the dialog — there the frame FILLS the sheet and the
     body scrolls inside it, so the pinned foot sits on the sheet's bottom
     edge; a frame grown to the document carried the foot off-screen. */
  useEffect(() => {
    if (bridge === undefined || ctx.presentation === "dialog") return;
    const send = (): void => bridge.sendResize(document.documentElement.offsetHeight);
    send();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(send);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, [bridge, pinnedCount, ctx.presentation]);

  /* B517 — Esc inside the dialog's frame closes the sheet (a keypress in
     our document never reaches the host's <dialog>). */
  useEffect(() => {
    if (bridge === undefined || ctx.presentation !== "dialog") return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") bridge.sendClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [bridge, ctx.presentation]);

  const config = useMemo(() => readConfig(ctx), [ctx]);
  /* B169 — does this page know whose business it is? Decided BEFORE the
     first call: an unconnected boot asks for nothing and shows ONE face. */
  const boot = useMemo(
    () => classifyBoot({ context: ctx, envTenantId: config.tenantId }),
    [ctx, config.tenantId],
  );
  /* B181 (RULED 2026-09-21) — a keyed, unconnected boot asks the public
     resolve which business it belongs to BEFORE anything else. While the
     one answer is in flight the page wears the loading face (never a
     flash of "isn't connected" it might be about to contradict); a hit
     completes the context (tenant + channel) and the ordinary boot takes
     over; a miss keeps B169's teaching face — honest, and now final. */
  const [resolvingSiteKey, setResolvingSiteKey] = useState<boolean>(() => needsSiteKeyResolve(classifyBoot({ context: ctx, envTenantId: config.tenantId })));
  useEffect(() => {
    if (!needsSiteKeyResolve(boot) || ctx.siteKey === undefined) {
      setResolvingSiteKey(false);
      return;
    }
    let gone = false;
    setResolvingSiteKey(true);
    void resolveSiteKey(config.apiBaseUrl, ctx.siteKey).then((channel) => {
      if (gone) return;
      if (channel !== undefined) setCtx((prev) => mergeResolvedChannel(prev, channel));
      setResolvingSiteKey(false);
    });
    return () => {
      gone = true;
    };
  }, [boot, ctx.siteKey, config.apiBaseUrl]);
  const session = useMemo(() => {
    let storage: Storage | null = null;
    try {
      storage = window.sessionStorage;
    } catch {
      storage = null;
    }
    return createSession(storage);
  }, []);
  const api = useMemo(
    () =>
      createApiClient({
        baseUrl: config.apiBaseUrl,
        tenantId: config.tenantId,
        token: () => session.token(),
      }),
    [config, session],
  );

  /* B144 — the brand is a BOOT fact, not a fetch side effect: a reload
     with a live token never mints, so the last mint's remembered theme is
     recalled and painted deterministically before any request fires. Host
     init hints still win per the sink's ownership rules. */
  const storedTheme = useMemo(() => {
    let storage: Storage | null = null;
    try {
      storage = window.sessionStorage;
    } catch {
      storage = null;
    }
    return recallChannelTheme(storage);
  }, []);
  useEffect(() => {
    if (storedTheme === undefined) return;
    applyChannelTheme(themeSink, storedTheme);
    if (storedTheme.logoUrl !== undefined) setBrandLogoUrl(storedTheme.logoUrl);
    if (storedTheme.headline !== undefined) setHeadline(storedTheme.headline);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boot-once by design
  }, []);

  /* B20b(h) — the guest door: every buyer call rides a live CUSTOMER token. */
  const gate = useMemo(
    () =>
      createGuestGate({
        api,
        session,
        channelId: config.channelId,
        onTheme: (theme: ChannelThemeLike) => {
          applyChannelTheme(themeSink, theme);
          setBrandLogoUrl(theme.logoUrl);
          setHeadline(theme.headline);
          /* B144: remember what the mint delivered — the next reload paints it at boot. */
          try {
            rememberChannelTheme(window.sessionStorage, theme);
          } catch {
            /* memory-only sessions just re-mint */
          }
        },
        onPublishableKey: (pk: string) => {
          setPublishableKey(pk);
          try {
            rememberPublishableKey(window.sessionStorage, pk);
          } catch {
            /* memory-only sessions just re-mint */
          }
        },
        onStripeAccount: (acct: string) => {
          setStripeAccount(acct);
          try {
            rememberStripeAccount(window.sessionStorage, acct);
          } catch {
            /* memory-only sessions just re-mint */
          }
        },
        onBusinessName: (name: string) => {
          setBusinessName(name);
          try {
            rememberBusinessName(window.sessionStorage, name);
          } catch {
            /* memory-only sessions just re-mint */
          }
        },
        onVenue: (facts: VenueFacts) => {
          setVenue(facts);
          try {
            rememberVenue(window.sessionStorage, facts);
          } catch {
            /* memory-only sessions just re-mint */
          }
        },
        onGroupThreshold: (threshold: number) => {
          setGroupThreshold(threshold);
          try {
            rememberGroupThreshold(window.sessionStorage, threshold);
          } catch {
            /* memory-only sessions just re-mint */
          }
        },
        onGroupsContactName: (name: string) => {
          setGroupsContactName(name);
          try {
            rememberGroupsContactName(window.sessionStorage, name);
          } catch {
            /* memory-only sessions just re-mint */
          }
        },
        onGtmContainerId: (id: string) => {
          setHostedContainerId(id);
          try {
            rememberGtmContainerId(window.sessionStorage, id);
          } catch {
            /* memory-only sessions just re-mint */
          }
        },
        onTenantTypeAbsent: () => {
          setTenantType(undefined);
          forgetTenantType(window.sessionStorage);
        },
        /* B621 — the mint is authoritative for the tenant's clock too. */
        onClock: (facts: ClockFacts) => {
          setClock(facts);
          try {
            rememberClock(window.sessionStorage, facts);
          } catch {
            /* memory-only sessions just re-mint */
          }
        },
        onClockAbsent: () => {
          setClock(undefined);
          forgetClock(window.sessionStorage);
        },
        onTenantType: (t: string) => {
          if (isTenantType(t)) {
            setTenantType(t);
            try {
              rememberTenantType(window.sessionStorage, t);
            } catch {
              /* memory-only sessions just re-mint */
            }
          }
        },
      }),
    [api, session, config.channelId, themeSink],
  );
  /* B403 403-low-note — the refused-bearer heal rides every authed call
     (lib/bearerHeal.ts): a server-refused gst bearer re-mints once and
     replays, instead of 403ing every read on the page. */
  const authed = useMemo<ApiClient>(() => createBearerHealingClient({ api, session, gate }), [api, session, gate]);

  /* B379 (c) — the visit funnel's coarse journey marks. Fire-and-forget,
     once per page life; the server dedups per guest per UTC day. */
  // B525 — each mark names its shell; distribution + presentation are fixed at load.
  const funnelSurface = funnelSurfaceOf(ctx.distribution, ctx.presentation);
  const funnel = useMemo(
    () => createFunnelReporter({ api: authed, channelId: config.channelId, surface: funnelSurface }),
    [authed, config.channelId, funnelSurface],
  );

  /* B524 — GA4 ecommerce events for the tenant's OWN GTM container.
     Embedded: relayed to the HOST page's data layer (the bridge queues until
     the host is pinned). Hosted: this page's own data layer, only for a channel
     that names a container — the channel-config field is leg 2, so until it
     lands the hosted sink is absent and nothing is pushed. Every event is
     validated (closed set, strict, no PII) inside the tracker.
     Leg 2 (RULED 2026-09-23): the container id rides the guest mint (set
     under Settings → Integrations) and the HOSTED page loads it — the
     embedded frame never does. */
  useEffect(() => {
    if (ctx.distribution !== "hosted") return;
    installGtm(window as unknown as Parameters<typeof installGtm>[0], hostedContainerId);
  }, [ctx.distribution, hostedContainerId]);
  const tracker = useMemo<Tracker>(() => {
    let storage: Storage | null = null;
    try {
      storage = window.sessionStorage;
    } catch {
      storage = null;
    }
    const sink =
      ctx.distribution === "embedded"
        ? bridge !== undefined
          ? relaySink(bridge)
          : undefined
        : hostedSink(window as unknown as Parameters<typeof hostedSink>[0], hostedContainerId);
    return createTracker({ sink, context: contextFor(ctx, config.channelId), storage });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the shell, not the whole context
  }, [bridge, ctx.distribution, ctx.presentation, config.channelId, hostedContainerId]);
  /* The SERVER's last cart read, the baseline add/remove are measured from.
     A reload onto an existing cart has no baseline until its first read —
     rehydrating lines is not the buyer adding them. */
  const serverLines = useRef<readonly LineLike[] | undefined>(session.cartId() === undefined ? [] : undefined);
  const appliedCoupon = useRef<string | undefined>(undefined);

  const [flow, dispatch] = useReducer(reduceFlow, undefined, initialFlow);
  const [products, setProducts] = useState<readonly ProductSummary[]>([]);
  /* B521 — the product read's photos + whole description, per product, once
     asked. The list card carries photos[0] and a first sentence only. */
  const [productMedia, setProductMedia] = useState<Readonly<Record<string, ProductMedia>>>({});
  const [productNames, setProductNames] = useState<Readonly<Record<string, string>>>({});
  const [slots, setSlots] = useState<readonly SlotSummary[]>([]);
  /* B615 leg 3b — every slot age the page has read (slot.list answers), kept
     across product switches so a cart line's showtime age outlives its sheet. */
  const [slotAges, setSlotAges] = useState<ReadonlyMap<string, number>>(new Map());
  const rememberSlotAges = useCallback((read: readonly SlotSummary[]) => {
    const aged = read.filter((s) => s.minAge !== undefined);
    if (aged.length === 0) return;
    setSlotAges((prev) => {
      const next = new Map(prev);
      for (const s of aged) next.set(s.slotId, s.minAge!);
      return next;
    });
  }, []);
  useEffect(() => rememberSlotAges(slots), [slots, rememberSlotAges]);
  const [availability, setAvailability] = useState<Readonly<Record<string, readonly PoolAvailability[]>>>({});
  /* B31h(a): slotId → the server's §2.4 verdict (TICKETS_FIRST calendars). */
  const [fits, setFits] = useState<Readonly<Record<string, boolean>>>({});
  const [dayKey, setDayKey] = useState<string | undefined>(undefined);
  const [lines, setLines] = useState<readonly CartLine[]>([]);
  /* B545 — the add plan reads the mirror at press time (the merge rule). */
  const linesRef = useRef<readonly CartLine[]>([]);
  linesRef.current = lines;
  const [total, setTotal] = useState<Money | undefined>(undefined);
  /* B170 (§10.2): the fired promotions, named — rendered beside the total. */
  const [adjustments, setAdjustments] = useState<readonly CartAdjustment[]>([]);
  /* B629 (c): each fee the buyer pays, then Tax — the wire's own named charges. */
  const [charges, setCharges] = useState<readonly ChargeRow[]>([]);
  /* B629 (c) — the Done card's breakdown, from the CONFIRMED order's own
     read (frozen facts), never the cart's last answer. */
  const [doneCharges, setDoneCharges] = useState<readonly ChargeRow[]>([]);
  const [promoApplied, setPromoApplied] = useState(false);
  /* B312 — the OTHER half of `cart.apply_promo`'s answer. `applied` is not
     "did the call succeed": the call succeeds and the answer is no, and that
     branch had no sentence, so a rejected code left the cart byte-identical
     to before the press. */
  const [promoRefusal, setPromoRefusal] = useState<string | undefined>(undefined);
  const [buyerEmail, setBuyerEmail] = useState("");
  /* B415: NAME required (refused in words on the pay press), PHONE optional. */
  const [buyerName, setBuyerName] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [nameRefusal, setNameRefusal] = useState<string | undefined>(undefined);
  /* B511 — the email a wallet did not share, asked for at its field. */
  const [emailRefusal, setEmailRefusal] = useState<string | undefined>(undefined);
  /* B603 — the optional phone, refused only when a typed value cannot be kept. */
  const [phoneRefusal, setPhoneRefusal] = useState<string | undefined>(undefined);
  /* B615 leg 2 — the pay step's acknowledgement tick and its press-refusal. */
  const [ackTicked, setAckTicked] = useState(false);
  const [acknowledgeRefusal, setAcknowledgeRefusal] = useState<string | undefined>(undefined);
  /* B616 leg 3a — the pay step's Insider opt-in (unticked until the buyer ticks it). */
  const [optedIn, setOptedIn] = useState(false);
  /* B616 leg 3b — the "Become an Insider" band: its field, its phase, its refusal. */
  const [insiderEmail, setInsiderEmail] = useState("");
  const [insiderPhase, setInsiderPhase] = useState<"idle" | "busy" | "joined">("idle");
  const [insiderError, setInsiderError] = useState<string | undefined>(undefined);
  /* B511 — the wallets this device can serve (the Express Checkout Element's ready read). */
  const [wallets, setWallets] = useState<readonly string[]>([]);
  const [entitlements, setEntitlements] = useState<readonly TicketView[]>([]);
  /* B115 — the human confirmation code, read off order.get once confirmed. */
  const [confirmationCode, setConfirmationCode] = useState<string | undefined>(undefined);
  /* B68(b) — departure labels captured at pick time, keyed by slotId, for the order summary. */
  const [slotLabels, setSlotLabels] = useState<Readonly<Record<string, string>>>({});
  /* B332 — the refusal line carries the step it is about, so leaving that
     step retires it instead of following the buyer to the card form. */
  const [notice, setNotice] = useState<BuyerNotice | undefined>(undefined);
  const [deferred, setDeferred] = useState<DeferredPaymentGateway | undefined>(undefined);
  const [gatewayNotice, setGatewayNotice] = useState<string | undefined>(undefined);
  const [paying, setPaying] = useState(false);
  /* B397 leg (3) — the channel's publishable key: mint-delivered, recalled at
     boot (B144's argument, one seam over). */
  const [publishableKey, setPublishableKey] = useState<string | undefined>(() => {
    try {
      return recallPublishableKey(window.sessionStorage);
    } catch {
      return undefined;
    }
  });
  /* B91 GAP-6 — the tenant's onboarded connected account, same seam. */
  const [stripeAccount, setStripeAccount] = useState<string | undefined>(() => {
    try {
      return recallStripeAccount(window.sessionStorage);
    } catch {
      return undefined;
    }
  });
  /* The hold chip's clock: ticks only while a hold is live. */
  const [now, setNow] = useState(() => Date.now());
  /* B31d — browse-step UI state: the picked card and the honest loading facts.
     B31h — the browse walk is staged: pick a card, then (per the card's
     flowOrder config) "when" (departure) and "compose" (party steppers) in
     either order; untimed products go straight to compose. */
  const [picked, setPicked] = useState<{ readonly productId: string } | undefined>(undefined);
  /* B57 — journey stages extend the walk: outbound when/compose, then (round
     trip) return when/compose. Non-journey products never leave the first two. */
  const [browseStage, setBrowseStage] = useState<"when" | "compose" | "returnWhen" | "returnCompose">("when");
  const [comp, setComp] = useState<Composition>(emptyComposition());
  /* B549 leg (2) — the group interrupt's facts: the channel's threshold
     (the mint's wire fact, recalled beside the token per B144) and the
     inquiry walk's own state. The clientKey is minted when the form OPENS,
     so a double-pressed SEND is one inquiry (Inv 4). */
  const [groupThreshold, setGroupThreshold] = useState<number | undefined>(() => {
    try {
      return recallGroupThreshold(window.sessionStorage);
    } catch {
      return undefined;
    }
  });
  // B549 (9) — the desk's first name, from the same mint, persisted beside the token.
  const [groupsContactName, setGroupsContactName] = useState<string | undefined>(() => {
    try {
      return recallGroupsContactName(window.sessionStorage);
    } catch {
      return undefined;
    }
  });
  const [groupStage, setGroupStage] = useState<"form" | "sent" | undefined>(undefined);
  const [groupDraft, setGroupDraft] = useState<GroupInquiryDraft | undefined>(undefined);
  const [groupKey, setGroupKey] = useState<string | undefined>(undefined);
  const [groupBusy, setGroupBusy] = useState(false);
  /* B589 (R18) — a refused send: a sentence for the notice, and/or pins at the fields it was about. */
  const [groupError, setGroupError] = useState<InquiryRefusal | undefined>(undefined);
  const [chosenSlot, setChosenSlot] = useState<
    { readonly slotId: string; readonly label?: string; readonly start?: number } | undefined
  >(undefined);
  /* B57 — the trip under composition (B403 #28: entered from a route CARD,
     not a planner; ret joins when the walk's trip-type pill picks ROUND TRIP). */
  const [trip, setTrip] = useState<{ readonly outbound: ProductSummary; readonly ret?: ProductSummary } | undefined>(undefined);
  const [retComp, setRetComp] = useState<Composition>(emptyComposition());
  const [retSlot, setRetSlot] = useState<{ readonly slotId: string; readonly label?: string } | undefined>(undefined);
  const [adding, setAdding] = useState(false);
  /* B338 — the cart's ONE in-flight field: the KEY of the act in the air
     (`cart.remove:${cartId}:${lineId}`, posData's shape), or undefined at
     rest. Single-valued by construction, so exactly one door lights and one
     live region is the right number rather than a compromise.

     The ref beside it is not a second source of truth — `beginCartAct` and
     `endCartAct` write BOTH or neither, which is the same reason B336's
     `run` sets `busy` and `busyAction` together. The ref exists because a
     React state read inside a callback is a snapshot: two presses landing in
     the same tick both see `undefined` and both start. The shut door is the
     cure a buyer can see; this is the one that holds where a `disabled`
     attribute does not reach. */
  const [cartBusy, setCartBusy] = useState<string | undefined>(undefined);
  const cartBusyRef = useRef<string | undefined>(undefined);
  const beginCartAct = useCallback((act: CartAct, cartId: string, lineId?: string): boolean => {
    if (!admitsCartAct(cartBusyRef.current)) return false;
    const key = cartActKey(act, cartId, lineId);
    if (key === undefined) return false;
    cartBusyRef.current = key;
    setCartBusy(key);
    return true;
  }, []);
  const endCartAct = useCallback((): void => {
    cartBusyRef.current = undefined;
    setCartBusy(undefined);
  }, []);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  /* B521 — the picked card's product.calendar answer; keyed to its product
     so a stale read can never price another card's days. */
  const [calPrices, setCalPrices] = useState<{ productId: string; pricing: CalendarPricingWire } | undefined>(undefined);
  const preselectConsumed = useRef(false);
  /* B403 #23 (a) — a preselect that names no live product says WHY the page
     shows everything (§10), instead of silently degrading to the catalog. */
  const [preselectGone, setPreselectGone] = useState<string | undefined>(undefined);

  /* B31g: the wire's operator vocabulary NEVER renders — every ApiError
     passes through the buyer-language translation (refusals.ts). */
  /* B31g: the kernel's §4.2 order-wide walls, spoken at the offending
     lines BEFORE checkout can refuse (the kernel stays the wall). */
  const cartHints = useMemo(() => cartIssueHints(lines, products), [lines, products]);
  /* B523 — opening CART in the dialog walk re-reads availability for every
     slotted line, so a line that no longer fits is marked HERE, before
     Continue is enabled — never discovered at Pay. */
  const [cartAvail, setCartAvail] = useState<Readonly<Record<string, readonly { readonly poolId: string; readonly available: number }[]>>>({});
  const [cartAvailChecked, setCartAvailChecked] = useState(false);

  /* B403 #28 (75:2) — the frame's booking page is ONE CARD PER PRODUCT,
     routes included: nothing leaves the grid. Groups survive only to answer
     returnCardFor (the walk's ROUND TRIP pill). */
  const groups = useMemo(() => routeGroups(products), [products]);
  /* B170: "from <min>" on cards whose journey family prices a ticket differently. */
  const priceFacts = useMemo(() => cardPriceFacts(products), [products]);

  /* B238(a): the catalog's ticket-type DISPLAY names, keyed by the wire id
     the cart lines carry — the same join summaryLines makes for the
     confirmation, hoisted so the CART can speak words too. */
  const variantNames = useMemo(() => {
    const out: Record<string, string> = {};
    for (const p of products) for (const v of p.variants) out[v.variantId] = v.name;
    return out;
  }, [products]);

  /* B332 — the funnel is told WHICH screen the sentence is true on. It is
     the screen the buyer LANDS on, not the one they were leaving: checkout
     refuses from CHECKOUT_IN_FLIGHT and dispatches CHECKOUT_REFUSED, which
     puts the buyer back on the cart, and that is where its words belong. */
  const report = useCallback((screen: ScreenKind, error: unknown) => {
    if (error instanceof ApiError) {
      setNotice({ screen, text: translateRefusal(error), reference: error.requestId });
    } else if (error instanceof ApiRequestError) {
      /* B169(b): a refusal from before the wire is a fault in the page —
         it must not wear the buyer's "try again". */
      setNotice({ screen, text: PAGE_MISCONFIGURED });
    } else setNotice({ screen, text: UNKNOWN_FAILURE });
  }, []);

  /* §7 step 1 — browse the channel's cards (B20b(a)). */
  useEffect(() => {
    /* B169: no business, no ask. Zero requests leave an unconnected page,
       and the catalog never lands "empty" about a catalog nobody read. */
    if (boot.kind !== "connected") {
      setCatalogLoading(false);
      return;
    }
    authed
      .call("catalog.list", { channelId: config.channelId })
      .then((res) => {
        const { products: list } = res as { products: readonly ProductSummary[] };
        setProducts(list);
        setProductNames(Object.fromEntries(list.map((p) => [p.productId, p.name])));
      })
      .catch((error: unknown) => report("browse", error))
      .finally(() => setCatalogLoading(false));
  }, [authed, boot.kind, config.channelId, report]);

  /* B379 (c) — the three journey marks. VISIT rides the same connected
     gate as the first catalog ask (B169: zero requests leave an
     unconnected page). VIEW is a product face opening — picked or a
     planned trip. CHECKOUT is the flow leaving BROWSE: on the merged
     cart & pay face (B397 leg (3)) the cart IS the checkout, so the
     first line added is the moment the buyer starts it. PAID is never
     marked from here — the platform's own orders carry it. */
  useEffect(() => {
    if (boot.kind !== "connected") return;
    funnel.mark("visit");
  }, [boot.kind, funnel]);
  useEffect(() => {
    if (picked !== undefined || trip !== undefined) funnel.mark("view");
  }, [picked, trip, funnel]);
  useEffect(() => {
    if (flow.phase !== "BROWSE") funnel.mark("checkout");
  }, [flow.phase, funnel]);
  /* B594 (1) — a cart that holds a line is "Cart built"; the hold (cart.checkout) is the orders' step. */
  useEffect(() => {
    if (lines.length > 0) funnel.mark("cart");
  }, [lines.length, funnel]);

  /* B524 — the list, the product face, the cart. Each is marked sent only
     when a sink took it (before the host pin the tracker has none). The
     dialog frame shows no list — its product arrives by preselect. */
  const listTracked = useRef(false);
  useEffect(() => {
    if (listTracked.current || products.length === 0 || ctx.presentation === "dialog") return;
    if (tracker.track("view_item_list", { item_list_id: config.channelId, items: listItems(products, config.channelId) }))
      listTracked.current = true;
  }, [products, tracker, config.channelId, ctx.presentation]);
  const viewedProduct = useRef<string | undefined>(undefined);
  const viewingId = trip?.outbound.productId ?? picked?.productId;
  useEffect(() => {
    if (viewingId === undefined) {
      viewedProduct.current = undefined;
      return;
    }
    if (viewedProduct.current === viewingId) return;
    const product = products.find((p) => p.productId === viewingId);
    if (product === undefined) return;
    const value = productValue(product) ?? { amount: 0, currency: config.currency };
    if (tracker.track("view_item", { currency: value.currency, value: gtmAmount(value), items: [productItem(product)] }))
      viewedProduct.current = viewingId;
  }, [viewingId, products, tracker, config.currency]);
  const cartViewed = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (flow.phase !== "CART") {
      cartViewed.current = undefined;
      return;
    }
    if (cartViewed.current === flow.cartId || lines.length === 0) return;
    const value = total ?? linesValue(lines, config.currency);
    if (tracker.track("view_cart", { currency: value.currency, value: gtmAmount(value), items: lineItems(lines, variantNames) }))
      cartViewed.current = flow.cartId;
  }, [flow, lines, total, tracker, variantNames, config.currency]);
  useEffect(() => {
    if (lines.length === 0 && session.cartId() === undefined) serverLines.current = [];
  }, [lines, session]);

  const openCart = useCallback(async (): Promise<string> => {
    const existing = session.cartId();
    if (existing !== undefined) return existing;
    const cartId = `cart-${crypto.randomUUID()}`;
    await authed.call("cart.create", { cartId, channelId: config.channelId, currency: config.currency });
    session.setCartId(cartId);
    return cartId;
  }, [authed, config, session]);

  /* B20b(c) — the server's cart is the cart; the mirror rehydrates from it. */
  const refreshCart = useCallback(
    async (cartId: string) => {
      const res = (await authed.call("cart.get", { cartId })) as {
        lines: readonly {
          lineId: string;
          productId: string;
          variantId: string;
          slotId?: string;
          slotStart?: number;
          quantity: number;
          amount: Money;
        }[];
        totals: { total: Money; adjustments: readonly CartAdjustment[]; fees?: readonly { feeId: string; label: string; amount: Money }[]; taxTotal?: Money };
      };
      const next = res.lines.map((l) => ({
        ...l,
        productName: productNames[l.productId] ?? l.productId,
      }));
      setLines(next);
      setTotal(res.totals.total);
      setAdjustments(res.totals.adjustments);
      setCharges(chargeRows(res.totals));
      /* B524 — add/remove are this read against the last one: after the fact. */
      const baseline = serverLines.current;
      serverLines.current = next;
      if (baseline !== undefined) {
        const { added, removed } = cartDeltas(baseline, next);
        const currency = res.totals.total.currency;
        if (added.length > 0)
          tracker.track("add_to_cart", { currency, value: gtmAmount(linesValue(added, currency)), items: lineItems(added, variantNames) });
        if (removed.length > 0)
          tracker.track("remove_from_cart", { currency, value: gtmAmount(linesValue(removed, currency)), items: lineItems(removed, variantNames) });
      }
    },
    [authed, productNames, tracker, variantNames],
  );

  const resetBrowse = useCallback(() => {
    setPicked(undefined);
    setComp(emptyComposition());
    setChosenSlot(undefined);
    setBrowseStage("when");
    setSlots([]);
    setAvailability({});
    setDayKey(undefined);
    setTrip(undefined);
    setRetComp(emptyComposition());
    setRetSlot(undefined);
  }, []);

  /* B31h — one cart.add per composed variant; every line rides the same
     slot so checkout's atomic hold covers the whole party (B4/B11). A
     mid-plan refusal leaves the server cart authoritative: we re-read it
     and surface the refusal — never a lying mirror. */
  const executePlan = useCallback(
    async (
      lines: readonly { productId: string; variantId: string; quantity: number; slotId?: string }[],
      options?: { readonly keepBrowse?: boolean },
    ) => {
      if (lines.length === 0) return;
      /* B332 — asking again retires the previous answer: a refused add that
         succeeds on the second press must not leave its own refusal
         standing over the cart it just filled. */
      setNotice(undefined);
      setAdding(true);
      /* B403 #37 — a remembered cartId the server no longer holds (TTL'd,
         abandoned elsewhere) must not trap the buyer: the wire's exact
         no-such-cart refusal retires the stored id and the SAME press
         replays once on a fresh cart; every other refusal — and a second
         refusal on the fresh cart — still surfaces. The orchestration is
         lib/cartHeal.ts, where the composed walls drive it. */
      const runPlan = async (cartId: string): Promise<void> => {
        /* B545 — the same ticket JOINS its line on the buyer surface (a
           set_quantity on the line already there); only the cart whose
           lines the mirror holds can match — a healed fresh cart has none. */
        const mirrored = cartId === session.cartId() ? linesRef.current : [];
        for (const step of mergeAddPlan(mirrored, lines)) {
          if (step.kind === "set") await authed.call("cart.set_quantity", { cartId, lineId: step.lineId, quantity: step.quantity });
          else await authed.call("cart.add", { cartId, ...step.line });
        }
        await refreshCart(cartId);
        /* B355 (widget): the fused card IS the browse state — adding at PAY
           must not wipe the card under the buyer's press. */
        if (options?.keepBrowse !== true) resetBrowse();
        dispatch({ type: "CART_OPENED", cartId });
      };
      let cartId: string | undefined;
      try {
        await runWithCartHeal(
          { open: openCart, clear: () => session.clearCartId(), healCheckedOut: dialogWalk },
          async (id) => {
            cartId = id;
            await runPlan(id);
          },
        );
      } catch (error) {
        if (cartId !== undefined) await refreshCart(cartId).catch(() => undefined);
        report("browse", error);
      } finally {
        setAdding(false);
      }
    },
    [authed, openCart, refreshCart, report, resetBrowse, session, dialogWalk],
  );

  /** Legacy direct add (variant-less cards, deep-linked slots): one line, quantity 1. */
  const addToCart = useCallback(
    async (productId: string, variantId: string | undefined, slotId: string | undefined) => {
      await executePlan([
        { productId, quantity: 1, ...(variantId !== undefined ? { variantId } : {}), ...(slotId !== undefined ? { slotId } : {}) } as {
          productId: string;
          variantId: string;
          quantity: number;
          slotId?: string;
        },
      ]);
    },
    [executePlan],
  );

  const removeLine = useCallback(
    async (lineId: string) => {
      const cartId = session.cartId();
      if (cartId === undefined) return;
      /* B338 — the second press of an open door is what made this the worst
         silence in the fleet: `cart.remove` is NOT idempotent, so the second
         call reached a line that was already gone, threw
         `CartLineNotFoundError`, and painted a refusal over a cart that was
         correct. Refusing to START the second act is the half a `disabled`
         attribute cannot reach. */
      if (!beginCartAct("cart.remove", cartId, lineId)) return;
      setNotice(undefined);
      try {
        await authed.call("cart.remove", { cartId, lineId });
        await refreshCart(cartId);
      } catch (error) {
        report("cart", error);
      } finally {
        endCartAct();
      }
    },
    [authed, beginCartAct, endCartAct, refreshCart, report, session],
  );

  const setLineQuantity = useCallback(
    async (lineId: string, quantity: number) => {
      const cartId = session.cartId();
      if (cartId === undefined) return;
      /* B391 — the floor is cart.remove, decided HERE rather than in the
         view: the platform refuses set_quantity(0) at the wire, and a
         surface that had to know that would be a second copy of the rule. */
      if (quantity <= 0) {
        await removeLine(lineId);
        return;
      }
      /* B338's door discipline, inherited: one act per line in flight.
         Unlike cart.remove this op IS idempotent by value, so a second
         press could not paint a false refusal — but a second press could
         still race the refresh and show a stale count, and the buyer
         reading the wrong number is the thing the door exists to prevent. */
      if (!beginCartAct("cart.set_quantity", cartId, lineId)) return;
      setNotice(undefined);
      try {
        await authed.call("cart.set_quantity", { cartId, lineId, quantity });
        await refreshCart(cartId);
      } catch (error) {
        report("cart", error);
      } finally {
        endCartAct();
      }
    },
    [authed, beginCartAct, endCartAct, refreshCart, removeLine, report, session],
  );

  /* B498 — one newest-wins opener for the whole departure surface: both
     open paths write the same `slots`/`fits`/spinner, so they share ONE
     gate — a fit open supersedes a plain open and vice versa. A stale
     settle (answer or refusal) writes nothing; only the newest clears the
     spinner. The drive lives in b498DeparturesRace.test.ts. */
  const openSheet = useMemo(
    () =>
      createDeparturesOpener({
        begin: () => {
          setNotice(undefined);
          setSlotsLoading(true);
          setFits({});
        },
        answer: (d) => {
          setSlots(d.slots);
          if (d.fits !== undefined) setFits(d.fits);
          if (d.availability !== undefined && Object.keys(d.availability).length > 0)
            setAvailability((prev) => ({ ...prev, ...d.availability }));
        },
        refuse: (error) => report("browse", error),
        settle: () => setSlotsLoading(false),
      }),
    [report],
  );

  const openDepartures = useCallback(
    (card: ProductSummary) => {
      setCalPrices(undefined);
      void loadCalendarPricing(authed, card, config.channelId).then((pricing) => {
        if (pricing !== undefined) setCalPrices({ productId: card.productId, pricing });
      });
      void openSheet(() => loadDepartures(authed, card));
    },
    [authed, config.channelId, openSheet],
  );

  /* B31h(a) (RULING 2026-07-24 #1) — the exact-fit calendar: one slot.fit
     carries the composed party; the server verdicts every departure through
     the §2.4/§6 matrix (journeys: every leg) and the SAME read delivers the
     availability, so the calendar paints in one round trip. A degraded read
     (no verdict) falls back to the plain shape — cart.add stays the wall. */
  const openFitDepartures = useCallback(
    (card: ProductSummary, composition: Readonly<Record<string, number>>) => {
      setCalPrices(undefined);
      void loadCalendarPricing(authed, card, config.channelId).then((pricing) => {
        if (pricing !== undefined) setCalPrices({ productId: card.productId, pricing });
      });
      void openSheet(() => loadFitDepartures(authed, card, config.channelId, composition));
    },
    [authed, config.channelId, openSheet],
  );

  /* B403 #28 — the walk's trip-type pill: ROUND TRIP joins the reverse
     card to the trip (honest absence — the pill only renders when the
     catalog sells one); ONE WAY drops it. The walk stays B57's:
     when → compose → [returnWhen → returnCompose]. */
  const chooseTripType = useCallback(
    (roundTrip: boolean) => {
      setTrip((prev) => {
        if (prev === undefined) return prev;
        if (!roundTrip) return { outbound: prev.outbound };
        const ret = returnCardFor(groups, prev.outbound);
        return ret === undefined ? prev : { outbound: prev.outbound, ret };
      });
    },
    [groups],
  );

  /* B31e (TRA-57) — availability rides the SELECTED day only, bounded.
     The eager shape (one slot.availability per ON_SALE slot, in parallel —
     270 for dev-ferry) exhausted the stage's Lambda concurrency pool and
     turned real-browser click-tests into API GW 503s while serial probes
     stayed green. A day is at most a handful of departures; unfetched
     availability renders as unknown, and cart.add stays the wall. */
  useEffect(() => {
    if (slots.length === 0) return;
    const groups = groupDepartures(slots);
    const day = groups.find((g) => g.key === dayKey) ?? groups[0];
    if (day === undefined) return;
    const missing = day.departures.filter((d) => availability[d.slotId] === undefined).map((d) => d.slotId);
    if (missing.length === 0) return;
    let stale = false;
    loadAvailability(authed, missing)
      .then((got) => {
        // An all-refused read merges nothing — no state change, no retry storm.
        if (!stale && Object.keys(got).length > 0) setAvailability((prev) => ({ ...prev, ...got }));
      })
      /* B332 — a BACKGROUND top-up stamps its screen but deliberately does
         not retire the buyer's standing answer: the buyer did not ask, so
         nothing they were told has been superseded. */
      .catch((error: unknown) => report("browse", error));
    return () => {
      stale = true;
    };
  }, [authed, slots, dayKey, availability, report]);

  /* B31d — picking a bound card opens the DATE→TIME step; the cart waits
     for a departure. B31c (TRA-52): the reads ride the CARD's scheduleIds —
     an unbound card ([]) skips the step and goes straight to the cart. */
  /* B31h — the staged walk. Untimed cards go straight to the composer; a
     variant-less card keeps the legacy one-tap add. Slotted cards follow
     their flowOrder config: SLOT_FIRST opens departures then composes for
     the chosen sailing; TICKETS_FIRST composes the party first, then picks
     a departure. */
  const pickProduct = useCallback(
    (productId: string) => {
      const card = products.find((p) => p.productId === productId);
      if (card === undefined) return;
      /* B403 #28 — a route card starts its journey walk directly (the
         planner is retired; the card is the route's door). One way by
         default — the trip-type pill rides the walk's departures step
         where the catalog sells the reverse O-D. */
      if (journeyOf(card) !== undefined) {
        setTrip({ outbound: card });
        setPicked({ productId: card.productId });
        setComp(emptyComposition());
        setRetComp(emptyComposition());
        setChosenSlot(undefined);
        setRetSlot(undefined);
        setSlots([]);
        setAvailability({});
        setDayKey(undefined);
        setBrowseStage("when");
        openDepartures(card);
        return;
      }
      if (card.variants.length === 0 && !needsDepartureStep(card)) {
        void addToCart(productId, undefined, undefined);
        return;
      }
      setPicked({ productId });
      setComp(emptyComposition());
      setChosenSlot(undefined);
      setSlots([]);
      setAvailability({});
      setDayKey(undefined);
      /* B521 — the product page asks for the whole product (every photo, the
         whole description). A refusal is quiet: the card's photo and first
         sentence already stand in, and nothing on the page depends on it. */
      if (productMedia[productId] === undefined) {
        authed
          .call("catalog.get", { channelId: config.channelId, productId })
          .then((res) => {
            const media = mediaFromProductRead(res);
            if (media !== undefined) setProductMedia((m) => ({ ...m, [productId]: media }));
          })
          .catch(() => undefined);
      }
      if (!needsDepartureStep(card)) {
        setBrowseStage("compose");
        return;
      }
      /* B397 leg (2) — the one-card tickets step (72:2) holds day, departure
         and counts at once, so the SLOT_FIRST / TICKETS_FIRST sequencing
         dissolves for a single-leg product: departures load at pick, always.
         (Journeys keep their walk — the frame does not draw a two-leg card.) */
      setBrowseStage("when");
      openDepartures(card);
    },
    [products, addToCart, openDepartures, productMedia, authed, config.channelId],
  );

  /* B614 — a poster's showtime chip opens that show's tickets step with the
     night and the showtime already chosen, once its departures have loaded. */
  const pendingShowtime = useRef<{ readonly productId: string; readonly slotId: string; readonly start: number; readonly nightOnly?: boolean } | undefined>(undefined);

  const pickDeparture = useCallback(
    (slotId: string) => {
      if (picked === undefined) return;
      /* B68(b): remember how this departure DISPLAYS — the confirmation summary reads it. */
      const pickedSlot = slots.find((sl) => sl.slotId === slotId);
      if (pickedSlot !== undefined) {
        setSlotLabels((m) => ({ ...m, [slotId]: slotDisplay(pickedSlot.start, whenOpts) }));
      }
      /* B57 — journey picks: the label carries the arrival when the final
         leg's sibling is known (display only; unknown stays unknown). */
      if (trip !== undefined) {
        const activeCard = browseStage === "returnWhen" ? trip.ret : trip.outbound;
        const j = activeCard === undefined ? undefined : journeyOf(activeCard);
        const s = slots.find((sl) => sl.slotId === slotId);
        const arrival = j === undefined ? undefined : journeyArrival(slots, j, slotId);
        /* B142: buyer words, one derivation — never a locale dump. */
        const label = s === undefined ? undefined : departureLabel(s.start, arrival, whenOpts);
        if (browseStage === "returnWhen") {
          setRetSlot({ slotId, ...(label !== undefined ? { label } : {}) });
          setBrowseStage("returnCompose");
        } else {
          setChosenSlot({ slotId, ...(label !== undefined ? { label } : {}), ...(s !== undefined ? { start: s.start } : {}) });
          setBrowseStage("compose");
        }
        return;
      }
      const card = products.find((p) => p.productId === picked.productId);
      if (card === undefined) return;
      const label = slots.find((sl) => sl.slotId === slotId)?.start;
      /* B397 leg (2): a pick is a CHOICE on the one card — CONTINUE is the
         only executor, so changing departure keeps the composed counts
         (72:2's own flow fact) and TICKETS_FIRST's auto-execute retires. */
      setChosenSlot({ slotId, ...(label !== undefined ? { label: slotDisplay(label, whenOpts) } : {}) });
    },
    [picked, products, slots, trip, browseStage, whenOpts],
  );

  useEffect(() => {
    const want = pendingShowtime.current;
    if (want === undefined || picked?.productId !== want.productId) return;
    if (!slots.some((sl) => sl.slotId === want.slotId)) return;
    pendingShowtime.current = undefined;
    setDayKey(sameDayKey(want.start));
    /* B614 (a′) — a calendar tile chooses the night; the buyer picks the showtime. */
    if (want.nightOnly !== true) pickDeparture(want.slotId);
  }, [slots, picked, pickDeparture]);

  /** The composer's single Add: SLOT_FIRST/untimed adds now; TICKETS_FIRST moves on to the departures. */
  const completeComposition = useCallback(() => {
    if (picked === undefined) return;
    /* B57 — the journey plan: one way adds now; a round trip walks on to the
       return sailing (same-day default, party prefilled per-direction) and
       adds BOTH directions as one plan — one cart, one atomic hold. */
    if (trip !== undefined) {
      if (browseStage === "returnCompose") {
        if (trip.ret === undefined || chosenSlot === undefined || retSlot === undefined) return;
        void executePlan(
          journeyAddPlan(
            { card: trip.outbound, slotId: chosenSlot.slotId, comp },
            { card: trip.ret, slotId: retSlot.slotId, comp: retComp },
          ),
        );
        return;
      }
      if (trip.ret !== undefined) {
        setRetComp(comp);
        setBrowseStage("returnWhen");
        if (chosenSlot?.start !== undefined) setDayKey(sameDayKey(chosenSlot.start));
        setSlots([]);
        setAvailability({});
        /* B31h(a): the return party is known here — the return calendar is
           exact-fit across every leg of the return O-D. */
        openFitDepartures(trip.ret, comp);
        return;
      }
      if (chosenSlot === undefined) return;
      void executePlan(journeyAddPlan({ card: trip.outbound, slotId: chosenSlot.slotId, comp }));
      return;
    }
    const card = products.find((p) => p.productId === picked.productId);
    if (card === undefined) return;
    if (!needsDepartureStep(card)) {
      void executePlan(addPlan(card, comp));
      return;
    }
    /* B397 leg (2): the one card gates its own door — CONTINUE is unreachable
       without a departure, so an undefined slot here is a no-op, never a
       silent add against no sailing. */
    if (chosenSlot === undefined) return;
    void executePlan(addPlan(card, comp, chosenSlot.slotId));
  }, [picked, products, comp, chosenSlot, executePlan, openDepartures, openFitDepartures, trip, browseStage, retComp, retSlot]);

  /* Deep links (§9.2 distribution 3): a preselected product opens its
     departures; a preselected slot goes straight to the cart. Once. */
  useEffect(() => {
    if (preselectConsumed.current || products.length === 0) return;
    /* B517 — the inline LIST never walks a product in place: a preselect
       there narrows the cards to that product, and its press opens the
       dialog, which consumes the preselect itself. */
    if (ctx.presentation === "list") return;
    const { productId, slotId } = ctx.preselect;
    if (productId === undefined) return;
    preselectConsumed.current = true;
    /* B403 #23 (a) — the demo's stale data-product measured this: an id the
       live catalog does not carry fell through to the full catalog with no
       word. The degrade stays (everything on sale IS the honest fallback);
       the silence goes. */
    if (!products.some((p) => p.productId === productId)) {
      setPreselectGone(productId);
      return;
    }
    if (slotId !== undefined) void addToCart(productId, undefined, slotId);
    else pickProduct(productId);
  }, [ctx.preselect, ctx.presentation, products, addToCart, pickProduct]);

  /* B397 leg (3) — the merged CART & PAY page (73:2). The flow machine is
     untouched: the LATE moment is one PRESS to the buyer and three §7 arrows
     underneath (checkout → intent → confirm), and the machine walks them
     all while this one screen stays mounted. */
  const mergedPay =
    flow.phase === "CART" || flow.phase === "CHECKOUT_IN_FLIGHT" || flow.phase === "PENDING_PAYMENT";

  /* B355 (checkout-widget leg, 58:238) — the §9.2 embed with ONE picked
     product renders the FUSED card: tickets + pay, no step navigation.
     Journeys and un-preselected embeds keep the step flow (recorded
     deviation on B355's line). The flow machine is untouched: PAY walks
     add → checkout → intent → confirm; the card stays mounted across
     every phase so the provider's element survives (leg (3)'s argument). */
  const widgetPicked = picked === undefined ? undefined : products.find((p) => p.productId === picked.productId);
  /* B615 leg 2 — the ONE line this purchase's policy facts compose (the cart's
     products, plus the widget's picked product before it lands as a line). */
  const ackPicks = [
    ...lines.map((l) => ({ productId: l.productId, slotId: l.slotId })),
    ...(widgetPicked !== undefined ? [{ productId: widgetPicked.productId, slotId: chosenSlot?.slotId }] : []),
  ];
  const acknowledgement = acknowledgementFor(products, ackPicks, slotAges);
  /* B616 leg 3a — a SHOW tenant's buyer is asked to join the club's list. */
  const optIn = optInFor(tenantType);
  /* B616 leg 3b — the band's JOIN: refused at the field, else subscriber.join
     (the uniform {} — the same thanks for a new address and a known one). */
  const joinInsider = useCallback(async (): Promise<void> => {
    if (insiderPhase !== "idle") return;
    const why = insiderEmailWhy(insiderEmail);
    if (why !== undefined) {
      setInsiderError(why);
      return;
    }
    setInsiderPhase("busy");
    const call = insiderJoinCall(insiderEmail, config.channelId);
    try {
      await authed.call("subscriber.join", call.body, { clientKey: call.clientKey });
      setInsiderPhase("joined");
    } catch {
      setInsiderPhase("idle");
      setInsiderError(INSIDER_FAILED);
    }
  }, [insiderPhase, insiderEmail, config.channelId, authed]);
  /* A different line is a different promise: the tick does not carry over. */
  useEffect(() => {
    setAckTicked(false);
    setAcknowledgeRefusal(undefined);
  }, [acknowledgement]);
  /* B523 — the CART step's availability re-read (kept beside dialogWalk:
     the walk it serves). Slotless carts pass at once; a dead read leaves
     the map empty, which claims nothing (§10 — not-read is never none). */
  useEffect(() => {
    if (!dialogWalk || flow.phase !== "CART") {
      setCartAvailChecked(false);
      return;
    }
    const slotIds = [...new Set(lines.map((l) => l.slotId).filter((v): v is string => v !== undefined))];
    if (slotIds.length === 0) {
      setCartAvail({});
      setCartAvailChecked(true);
      return;
    }
    let alive = true;
    setCartAvailChecked(false);
    void loadAvailability(authed, slotIds).then((m) => {
      if (!alive) return;
      setCartAvail(m);
      setCartAvailChecked(true);
    });
    return () => {
      alive = false;
    };
  }, [dialogWalk, flow.phase, lines, authed]);
  const dialogAvailHints = useMemo(
    () =>
      cartAvailabilityHints(lines, cartAvail, (l) =>
        l.slotId !== undefined && slotLabels[l.slotId] !== undefined
          ? slotLabels[l.slotId]
          : l.slotStart !== undefined
            ? slotDisplay(l.slotStart, whenOpts)
            : undefined,
      ),
    [lines, cartAvail, slotLabels, whenOpts],
  );
  const widgetMode =
    ctx.distribution === "embedded" &&
    !dialogWalk &&
    widgetPicked !== undefined &&
    trip === undefined &&
    (flow.phase === "BROWSE" || mergedPay);
  const widgetDisplayTotal = widgetMode && widgetPicked !== undefined ? displayTotal(widgetPicked, comp) : undefined;

  /* B403 #26 (e) — the cards' GREEN live fact ("Next departure 10:30 ·
     36 seats left"): a bounded read per departure-riding card (first six —
     the frame's grid page), ONE availability call for the next slot only.
     Every refusal is an absent fact, never a guessed one (§10). */
  /* B403 #22-extension — the buyer-voice slice: absent until the mint (or
     the boot recall) names a type; the standing register meanwhile. */
  const vocab: BuyerVocabulary | undefined = tenantType === undefined ? undefined : vocabularyFor(tenantType);
  const [nextFacts, setNextFacts] = useState<ReadonlyMap<string, string>>(new Map());
  useEffect(() => {
    if (widgetMode) return;
    const cards = products.filter((c) => c.scheduleIds.length > 0).slice(0, 6);
    if (cards.length === 0) return;
    let alive = true;
    void (async () => {
      const out = new Map<string, string>();
      for (const card of cards) {
        try {
          const d = await loadDepartures(authed, card);
          const at = Date.now();
          /* B403 #28 — a route card's green fact speaks its BOARDING leg,
             not whichever leg of the schedule departs next. */
          const cardJourney = journeyOf(card);
          const cardSlots = cardJourney === undefined ? d.slots : journeyDepartures(d.slots, cardJourney);
          const next = cardSlots
            .filter((sl) => sl.status === "ON_SALE" && sl.start >= at)
            .sort((a, b) => a.start - b.start)[0];
          let pools: readonly PoolAvailability[] = [];
          if (next !== undefined) {
            try {
              pools = ((await authed.call("slot.availability", { slotId: next.slotId })) as { pools: readonly PoolAvailability[] }).pools;
            } catch {
              /* count unread — the shorter honest line */
            }
          }
          const fact = nextDepartureFact(cardSlots, pools, at, vocab === undefined ? {} : { vocab });
          if (fact !== undefined) out.set(card.productId, fact);
        } catch {
          /* no read, no fact */
        }
      }
      if (alive) setNextFacts(out);
    })();
    return () => {
      alive = false;
    };
  }, [products, authed, widgetMode, vocab]);

  /* B614 — the SHOW face's poster wall: each show's ON_SALE slots ahead (the
     calendar's months, leg (a′)) and, for those in the next 14 days, one
     availability read each (bounded). A refused read is
     an absent fact — the poster says no nights or an open chip, never a
     guessed "few left". */
  const showFace = !widgetMode && tenantType === "SHOW";
  const [posterFeeds, setPosterFeeds] = useState<ReadonlyMap<string, PosterFeed>>(new Map());
  const [posterLoading, setPosterLoading] = useState(false);
  const posterNow = useRef(Date.now());
  const [showView, setShowView] = useState<ShowView>("posters");
  const [showMonth, setShowMonth] = useState(() => thisMonthKey(Date.now()));
  useEffect(() => {
    if (!showFace) return;
    const cards = products.filter((c) => c.scheduleIds.length > 0);
    if (cards.length === 0) return;
    let alive = true;
    setPosterLoading(true);
    void (async () => {
      const now = Date.now();
      posterNow.current = now;
      const end = now + POSTER_WINDOW_DAYS * 86_400_000;
      const out = new Map<string, PosterFeed>();
      for (const card of cards) {
        try {
          const d = await loadDepartures(authed, card);
          const live = d.slots.filter((sl) => sl.status === "ON_SALE" && sl.start >= now);
          const soon = live.filter((x) => x.start < end);
          const avail = await loadAvailability(authed, soon.map((x) => x.slotId));
          out.set(card.productId, { slots: live, nights: posterNights(live, avail, now, whenOpts), availability: avail });
        } catch {
          /* no read, no nights */
        }
        if (alive) setPosterFeeds(new Map(out));
      }
      if (alive) setPosterLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [products, authed, showFace, whenOpts]);

  /* The kernel's total wins the quote once known (§10.1). */
  const quoteTotal = total ?? widgetDisplayTotal;
  /* B478 — effects key on these VALUES: `quoteTotal` is a fresh object per
     render once setTotal lands, and keying the mount/re-quote effects on
     its identity is what unmounted and re-quoted the payment element MID-
     CONFIRM (the "Your card number is incomplete" wedge). */
  const quoteAmount = quoteTotal?.amount;
  const quoteCurrency = quoteTotal?.currency;

  /* §7 step 4, deferred (B397 leg (3)): elements are QUOTED from the display
     total at page load — no order, no intent, no clientSecret exists yet. */
  const deferredFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!(mergedPay || widgetMode) || quoteAmount === undefined || quoteCurrency === undefined) return;
    const wanted = `${publishableKey ?? ""}:${stripeAccount ?? ""}:${quoteCurrency}:${quoteAmount > 0 ? "priced" : "unpriced"}`;
    if (deferredFor.current === wanted) return;
    deferredFor.current = wanted;
    (async () => {
      const factory = await loadStripeFactory();
      /* B177: the element lives in Stripe's iframe — the app theme reaches
         it only as an appearance derived from our tokens. */
      const built = createDeferredPaymentGateway({
        factory,
        publishableKey,
        ...(stripeAccount === undefined ? {} : { stripeAccount }),
        amount: quoteAmount,
        currency: quoteCurrency,
        appearance: documentPaymentAppearance(),
      });
      if (built.gateway !== undefined) {
        setDeferred(built.gateway);
        setGatewayNotice(undefined);
      } else {
        setDeferred(undefined);
        setGatewayNotice(built.unavailable);
      }
    })();
  }, [mergedPay, widgetMode, publishableKey, stripeAccount, quoteAmount, quoteCurrency]);

  /* A moved total (promo fired, line changed) re-quotes the mounted element. */
  useEffect(() => {
    if (deferred === undefined || quoteAmount === undefined || quoteAmount <= 0) return;
    deferred.setAmount(quoteAmount);
  }, [deferred, quoteAmount]);

  /* B177: the hosted storefront ships a ThemeToggle, so the theme can flip
     with the element already mounted — re-derive and re-apply rather than
     leave a light card form on a dark page. */
  useEffect(() => {
    if (deferred === undefined) return;
    return observePaymentAppearance((appearance) => deferred.setAppearance(appearance));
  }, [deferred]);

  /* Mount the payment element once the screen holding its container renders.
     B397 walk reopen: this effect used to depend on the `flow` OBJECT, whose
     identity changes on every dispatch — so PAY's own CHECKOUT_OK re-ran the
     cleanup+mount cycle mid-confirm, and the second create('payment') threw
     Stripe's IntegrationError over a blank page. The deps are now the two
     FACTS the effect reads, the gateway reuses its one element across the
     cycle regardless, and a mount that still throws lands on the pay card's
     walled face instead of unmounting the tree. */
  const authorizedPending = flow.phase === "PENDING_PAYMENT" && flow.authorized;
  const quotePriced = quoteAmount !== undefined && quoteAmount > 0;
  useEffect(() => {
    /* B523 — in the dialog walk the pay card exists only on the PAY step:
       mounting into an absent container walls the gateway for nothing. */
    const holds = widgetMode ? quotePriced : dialogWalk ? flow.phase === "PENDING_PAYMENT" && lines.length > 0 : mergedPay && lines.length > 0;
    if (deferred === undefined || !holds) return;
    if (authorizedPending) return;
    try {
      deferred.mount("#payment-element");
    } catch (error) {
      setDeferred(undefined);
      setGatewayNotice("The card fields could not be drawn — reload the page to try again.");
      if (typeof reportError === "function") reportError(error);
      return;
    }
    return () => deferred.unmount();
  }, [deferred, mergedPay, widgetMode, dialogWalk, flow.phase, quotePriced, lines.length, authorizedPending]);

  /* The hold chip's own clock, seconds while a hold is live (72:2's chip). */
  useEffect(() => {
    if (flow.phase !== "PENDING_PAYMENT" || flow.authorized) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [flow]);

  /* B524 — add_payment_info: the buyer chose HOW to pay (card PAY, or a wallet sheet authorized). */
  const trackPaymentInfo = useCallback(
    (paymentType: "card" | "apple_pay" | "google_pay") => {
      const value = total ?? linesValue(lines, config.currency);
      tracker.track("add_payment_info", {
        currency: value.currency,
        value: gtmAmount(value),
        payment_type: paymentType,
        items: lineItems(lines, variantNames),
      });
    },
    [total, lines, config.currency, tracker, variantNames],
  );

  /**
   * B511 — the order mint BOTH presses share (card PAY and the wallet's
   * confirm): cart.checkout with the buyer (order + hold, Inv-4 keyed),
   * then payment.intent.create (Inv-4 keyed on the order). A retry on a
   * PENDING order skips straight to the intent. `reached` fires once an
   * order exists, so a later refusal lands on the payment step, not the cart.
   */
  /* B523 — the checkout half on its own: the Cart step's Continue IS this
     call (the hold + timer begin here), and mintOrder shares it so the
     hosted page's LATE press mints the same way it always has. The key
     stays deterministic per cart (Inv 4, the double-tap rule) and moves
     ONLY when a hold has expired: a replayed key would re-answer with the
     expired order, so the expiry return bumps the attempt. */
  const checkoutAttempt = useRef(0);
  const checkoutCart = useCallback(
    async (
      buyer: {
        readonly name: string;
        readonly email: string;
        readonly phone: string;
      },
      acknowledged = false,
      subscribe = false,
    ): Promise<
      { readonly orderId: string; readonly amount: Money } | { readonly refused: string; readonly atField?: "name" | "email" | "phone" }
    > => {
      const cartId = session.cartId();
      if (cartId === undefined) return { refused: "Your cart is missing — start again from the tickets." };
      const key = checkoutAttempt.current === 0 ? `checkout-${cartId}` : `checkout-${cartId}-r${checkoutAttempt.current}`;
      dispatch({ type: "CHECKOUT_STARTED", proposedKey: key });
      try {
        /* B415: the buyer fields ride only when typed; B594 (2): the shell rides too. */
        const res = (await authed.call(
          "cart.checkout",
          checkoutBody(cartId, buyer, funnelSurface, acknowledged, subscribe),
          { clientKey: key },
        )) as { orderId: string; totals: { total: Money; adjustments: readonly CartAdjustment[]; fees?: readonly { feeId: string; label: string; amount: Money }[]; taxTotal?: Money }; holdExpiresAt: number; held?: false };
        setTotal(res.totals.total);
        setAdjustments(res.totals.adjustments);
        setCharges(chargeRows(res.totals));
        dispatch({ type: "CHECKOUT_OK", orderId: res.orderId, holdExpiresAt: holdDeadline(res) });
        /* B524 — begin_checkout is the HOLD: cart.checkout answered. */
        tracker.track("begin_checkout", {
          currency: res.totals.total.currency,
          value: gtmAmount(res.totals.total),
          ...(appliedCoupon.current !== undefined ? { coupon: appliedCoupon.current } : {}),
          items: lineItems(lines, variantNames),
        });
        return { orderId: res.orderId, amount: res.totals.total };
      } catch (error) {
        dispatch({ type: "CHECKOUT_REFUSED" });
        /* CHECKOUT_REFUSED returns the buyer to the cart — that is where
           this sentence is true. */
        if (error instanceof ApiRequestError) {
          /* B603 — the client schema refused the body BEFORE the wire. A
             refusal naming a buyer-typed field is the buyer's to fix, at
             that field; only one naming no buyer field is the page's. */
          const atField = buyerFieldOf(error.fields);
          return atField !== undefined ? { refused: buyerFieldSentence(atField), atField } : { refused: PAGE_MISCONFIGURED };
        }
        return { refused: error instanceof ApiError ? translateRefusal(error) : UNKNOWN_FAILURE };
      }
    },
    [session, authed, tracker, lines, variantNames, funnelSurface],
  );

  const mintOrder = useCallback(
    async (
      buyer: { readonly name: string; readonly email: string; readonly phone: string },
      reached: () => void,
    ): Promise<PayMint> => {
      let orderId: string;
      let amount: Money;
      /* B615 leg 2 — the tick rides whichever write names the order here. */
      const acknowledged = acknowledgement !== undefined && ackTicked;
      /* B616 leg 3a — the opt-in rides the same write; never a refusal. */
      const subscribe = optIn !== undefined && optedIn;
      if (flow.phase === "PENDING_PAYMENT") {
        orderId = flow.orderId;
        if (total === undefined) return { refused: "The order's total is missing — reload the page." };
        amount = total;
        /* B545 (d) — the buyer attaches at PAY: Cart's Continue checked out
           with no buyer, so the wallet's details (or the typed fields) name
           the PENDING order here, before the intent. Idempotent by value. */
        if (buyer.email.trim() !== "") {
          const attach = buyerSetCall(orderId, buyer, acknowledged, subscribe);
          try {
            await authed.call("order.buyer.set", attach.body, { clientKey: attach.clientKey });
          } catch (error) {
            if (error instanceof ApiRequestError) {
              /* B603 — the same backstop as checkout's: a buyer field's
                 refusal rides that field, never the page-broken banner. */
              const atField = buyerFieldOf(error.fields);
              return atField !== undefined ? { refused: buyerFieldSentence(atField), atField } : { refused: PAGE_MISCONFIGURED };
            }
            return { refused: error instanceof ApiError ? translateRefusal(error) : UNKNOWN_FAILURE };
          }
        }
      } else {
        const minted = await checkoutCart(buyer, acknowledged, subscribe);
        if ("refused" in minted)
          return { refused: minted.refused, ...(minted.atField !== undefined ? { atField: minted.atField } : {}) };
        reached();
        orderId = minted.orderId;
        amount = minted.amount;
      }
      const res = (await authed.call(
        "payment.intent.create",
        { orderId, amount, provider: "stripe" },
        { clientKey: `intent-${orderId}` },
      )) as { clientSecret: string };
      /* B478 — hand the KERNEL's total back: when it differs from the
         page quote the gateway re-quotes and re-submits before confirm,
         so the intent and the element never disagree. */
      return { clientSecret: res.clientSecret, amount: amount.amount };
    },
    [flow, total, checkoutCart, authed, acknowledgement, ackTicked, optIn, optedIn],
  );

  /**
   * PAY — the LATE moment as ONE press (73:2). Field validation first
   * (elements.submit — no order is minted for a typo), then the mint:
   * cart.checkout (order + hold, Inv-4 keyed) and payment.intent.create
   * (Inv-4 keyed on the order), then confirm against the fresh secret.
   * A retry on a PENDING order skips straight to the intent (the replayed
   * clientKey re-delivers the same secret — B20b(d)).
   */
  const pay = useCallback(async () => {
    if (deferred === undefined) return;
    /* B415 (b): the tickets are booked under a name — refuse the press in
       words before anything is minted. A PENDING retry already has its
       order (the name went with the mint), so only the minting press asks. */
    /* B545 (d): in the dialog walk the buyer attaches HERE (Cart's Continue
       named nobody), so the Pay press asks for the name AND the email —
       the tickets need somewhere to go. The wallet path brings its own. */
    const attachesHere = flow.phase !== "PENDING_PAYMENT" || dialogWalk;
    const nameMissing = attachesHere && buyerName.trim() === "";
    const emailMissing = dialogWalk && buyerEmail.trim() === "";
    /* B603 — R19: what WAS typed is judged for what the field says, against
       the checkout contract's own schemas, while the field can still be
       seen — before this, `maya@` sailed into the client's schema parse and
       came back as "this page is broken". Emptiness rules are unchanged. */
    const typed = buyerFieldProblems({ name: buyerName, email: buyerEmail, phone: buyerPhone });
    const nameWhy = nameMissing ? "Add the name for the booking first — your tickets are booked under it." : typed.name;
    const emailWhy = emailMissing ? "Add the email for your tickets — it’s where we send them." : typed.email;
    setNameRefusal(nameWhy);
    setEmailRefusal(emailWhy);
    setPhoneRefusal(typed.phone);
    /* B615 leg 2 — the policy line must be ticked; the refusal sits at the box. */
    const ackWhy = acknowledgement !== undefined && !ackTicked ? ACKNOWLEDGE_REFUSAL : undefined;
    setAcknowledgeRefusal(ackWhy);
    if (nameWhy !== undefined || emailWhy !== undefined || typed.phone !== undefined || ackWhy !== undefined) return;
    setNotice(undefined);
    setPaying(true);
    trackPaymentInfo("card");
    let reachedPending = flow.phase === "PENDING_PAYMENT";
    try {
      const outcome = await deferred.confirmWith(() =>
        mintOrder({ name: buyerName.trim(), email: buyerEmail.trim(), phone: buyerPhone.trim() }, () => {
          reachedPending = true;
        }),
      );
      if (!outcome.ok) {
        if (outcome.atField !== undefined) {
          /* B603 — the backstop's refusal is about a field the buyer can
             see; it rides that field's own seam, and no payment failed. */
          if (outcome.atField === "name") setNameRefusal(outcome.message);
          else if (outcome.atField === "phone") setPhoneRefusal(outcome.message);
          else setEmailRefusal(outcome.message);
        } else {
          /* The sentence lands where the buyer IS: a refused checkout returned
             them to the cart; a refused confirm leaves a pending order. */
          setNotice({ screen: reachedPending ? "payment" : "cart", text: outcome.message });
          if (reachedPending) dispatch({ type: "PAYMENT_FAILED", at: Date.now() });
        }
      } else {
        /* Success is NOT confirmation: the provider holds the money; §7
           step 5 arrives via the webhook (B23b) and the poller observes it —
           the §11 kernel stays the source of truth. Authorized only walls
           the Pay button and the UI expiry arrow (B29 defers server-side). */
        setNotice(undefined);
        dispatch({ type: "PAYMENT_AUTHORIZED" });
      }
    } finally {
      setPaying(false);
    }
  }, [deferred, flow, dialogWalk, buyerEmail, buyerName, buyerPhone, mintOrder, trackPaymentInfo, acknowledgement, ackTicked]);

  /* B523 — the CART step's Continue IS cart.checkout (the item's ruling):
     the hold + timer begin here, and Pay opens with the strip counting.
     B545 (d) — RULED "move that portion to payment step": the checkout
     names NOBODY; the buyer attaches at Pay (order.buyer.set in mintOrder),
     so Express Checkout can sit above the fields and one press buys. */
  const dialogContinue = useCallback(async () => {
    if (flow.phase !== "CART") return;
    setNotice(undefined);
    const minted = await checkoutCart(NO_BUYER);
    if ("refused" in minted) setNotice({ screen: "cart", text: minted.refused });
  }, [flow.phase, checkoutCart]);

  /* B523 (370-2) — Edit on a cart line: back to that product's day & time
     with the line's picks loaded. The line LEAVES the cart (Continue would
     otherwise add a second copy) and the composer opens pre-filled; the
     edited version re-enters through the tickets step's own Continue. */
  const dialogEditLine = useCallback(
    (lineId: string) => {
      if (flow.phase !== "CART") return;
      const line = lines.find((l) => l.lineId === lineId);
      if (line === undefined) return;
      const card = products.find((p) => p.productId === line.productId);
      if (card === undefined) return;
      pickProduct(line.productId);
      if (line.variantId !== undefined) setComp(setQuantity(card, emptyComposition(), line.variantId, line.quantity));
      if (line.slotId !== undefined) {
        setChosenSlot({
          slotId: line.slotId,
          ...(line.slotStart !== undefined ? { label: slotDisplay(line.slotStart, whenOpts) } : {}),
        });
        if (line.slotStart !== undefined) setDayKey(sameDayKey(line.slotStart));
      }
      void removeLine(lineId);
      dispatch({ type: "SHOPPING_CONTINUED" });
    },
    [flow.phase, lines, products, pickProduct, removeLine],
  );

  /* B523 — a hold that runs out returns the sheet to CART with the lines
     kept ("Your hold ended — check availability again"): RESTARTED and
     CART_OPENED are both legal arrows, the cart survives server-side, and
     the attempt bump keeps the next Continue from replaying the expired
     order's idempotency key. Dialog walk only — the hosted page keeps its
     EXPIRED face. */
  useEffect(() => {
    if (!dialogWalk || flow.phase !== "EXPIRED") return;
    const cartId = session.cartId();
    checkoutAttempt.current += 1;
    setTotal(undefined);
    setAdjustments([]);
    setCharges([]);
    dispatch({ type: "RESTARTED" });
    if (cartId !== undefined) dispatch({ type: "CART_OPENED", cartId });
    setNotice({ screen: "cart", text: "Your hold ended — check availability again." });
  }, [dialogWalk, flow.phase, session]);

  /* B355 (widget) — PAY is the fused card's ONE press: from BROWSE it
     first lands the composed lines as the cart (keepBrowse — the card must
     not wipe under the buyer's finger), then walks the same LATE chain
     pay() owns. From CART/PENDING it is pay()'s own retry semantics,
     Inv-4 keys and all. */
  const widgetPay = useCallback(async () => {
    if (flow.phase === "BROWSE") {
      if (widgetPicked === undefined) return;
      if (needsDepartureStep(widgetPicked) && chosenSlot === undefined) return;
      await executePlan(addPlan(widgetPicked, comp, chosenSlot?.slotId), { keepBrowse: true });
      if (session.cartId() === undefined) return; // the add refused; its notice stands
    }
    await pay();
  }, [flow.phase, widgetPicked, chosenSlot, comp, executePlan, session, pay]);

  /*
   * B511 (356-2) — WALLET FIRST: the Express Checkout Element on the same
   * deferred group, one press = the order. The gateway owns the Stripe
   * choreography (submit → mint → confirm, the paymentFailed seams); this
   * container owns the §7 arrows and the buyer's words. The handlers read
   * through a ref so the ONE mounted element always sees this render's
   * state (flow, typed fields, the composed card) — never a stale closure.
   */
  const expressReached = useRef(false);
  const expressLatest = useRef<ExpressHandlers | undefined>(undefined);
  /* The wallet's own wall before B615: the same ONE statement PAY is walled on. */
  const expressWall = (): string | undefined => {
    if (!widgetMode)
      /* 73:2's own ONE statement (CartPayView's payWall), same order. */
      return cartHints.length > 0
        ? "Adjust your tickets to pay."
        : lines.length === 0
          ? "Add tickets to pay."
          : deferred === undefined
            ? (gatewayNotice ?? "Payment isn’t set up on this booking page yet.")
            : undefined;
    return widgetPicked === undefined
      ? "Pick your tickets to pay."
      : widgetPayWall({
          card: widgetPicked,
          comp,
          chosenSlotId: chosenSlot?.slotId,
          gatewayReady: deferred !== undefined,
          gatewayNotice,
          vocab,
        });
  };
  expressLatest.current = {
    typed: () => ({ name: buyerName, email: buyerEmail }),
    walled: () => {
      const wall = expressWall();
      /* B615 leg 2 — the wallet pays in one press, so the box is asked first. */
      if (wall === undefined && acknowledgement !== undefined && !ackTicked) {
        setAcknowledgeRefusal(ACKNOWLEDGE_REFUSAL);
        return ACKNOWLEDGE_REFUSAL;
      }
      return wall;
    },
    onReady: (methods) => setWallets(methods),
    onStart: (walletType) => {
      if (walletType === "apple_pay" || walletType === "google_pay") trackPaymentInfo(walletType);
      expressReached.current = flow.phase === "PENDING_PAYMENT";
      setNotice(undefined);
      setNameRefusal(undefined);
      setEmailRefusal(undefined);
      setPhoneRefusal(undefined);
      setPaying(true);
    },
    mint: async (buyer) => {
      /* From BROWSE the composed lines land as the cart first — widgetPay's
         own first arrow (keepBrowse: the card must not wipe mid-press). */
      if (flow.phase === "BROWSE" && widgetMode) {
        if (widgetPicked === undefined) return { refused: "Pick your tickets to pay." };
        await executePlan(addPlan(widgetPicked, comp, chosenSlot?.slotId), { keepBrowse: true });
        if (session.cartId() === undefined) return { refused: "Your tickets could not be held — try again." };
      }
      return mintOrder({ name: buyer.name, email: buyer.email, phone: buyerPhone.trim() }, () => {
        expressReached.current = true;
      });
    },
    onOutcome: (outcome) => {
      setPaying(false);
      if (outcome.ok) {
        /* The wallet's details ARE the booking's — the confirmation and the
           ticket email read these fields. */
        setBuyerName(outcome.buyer.name);
        setBuyerEmail(outcome.buyer.email);
        setNotice(undefined);
        dispatch({ type: "PAYMENT_AUTHORIZED" });
        return;
      }
      if ("missing" in outcome) {
        /* The ruling's second sentence: ask BEFORE the order — keep what the
           wallet did give, name the field it did not. */
        if (outcome.partial.name !== undefined && buyerName.trim() === "") setBuyerName(outcome.partial.name);
        if (outcome.partial.email !== undefined && buyerEmail.trim() === "") setBuyerEmail(outcome.partial.email);
        if (outcome.missing.includes("name")) setNameRefusal(outcome.message);
        else setEmailRefusal(outcome.message);
        return;
      }
      if (outcome.atField !== undefined) {
        /* B603 — the wallet's mint refused over a field the buyer can see
           below the wallet row: the sentence sits there, as the typed
           press's would. */
        if (outcome.atField === "name") setNameRefusal(outcome.message);
        else if (outcome.atField === "phone") setPhoneRefusal(outcome.message);
        else setEmailRefusal(outcome.message);
        return;
      }
      const reached = expressReached.current;
      setNotice({ screen: reached ? "payment" : flow.phase === "BROWSE" ? screen : "cart", text: outcome.message });
      if (reached) dispatch({ type: "PAYMENT_FAILED", at: Date.now() });
    },
  };
  useEffect(() => {
    /* Where the card element mounts, the wallet row mounts: the widget once
       priced, 73:2 once the cart holds lines (the payment effect's rule). */
    const holds = widgetMode ? quotePriced : dialogWalk ? flow.phase === "PENDING_PAYMENT" && lines.length > 0 : mergedPay && lines.length > 0;
    if (deferred === undefined || !holds || authorizedPending) return;
    const via: ExpressHandlers = {
      typed: () => expressLatest.current!.typed(),
      walled: () => expressLatest.current!.walled(),
      onReady: (m) => expressLatest.current!.onReady(m),
      onStart: () => expressLatest.current!.onStart(),
      mint: (b) => expressLatest.current!.mint(b),
      onOutcome: (o) => expressLatest.current!.onOutcome(o),
    };
    try {
      if (!deferred.mountExpress("#express-checkout-element", via)) return;
    } catch (error) {
      /* A wallet row that cannot draw leaves the card path standing. */
      setWallets([]);
      if (typeof reportError === "function") reportError(error);
      return;
    }
    return () => deferred.unmountExpress();
  }, [deferred, widgetMode, mergedPay, dialogWalk, flow.phase, lines.length, quotePriced, authorizedPending]);

  /* B355 (widget) — counts after the hold: the cart is authoritative once
     an attempt was made, so a stepper press drives the LINE (B391's rule)
     and the composition mirror follows for the card's own rows. */
  const widgetChange = useCallback(
    (variantId: string, quantity: number) => {
      if (widgetPicked === undefined) return;
      setComp((prev) => setQuantity(widgetPicked, prev, variantId, quantity));
      if (flow.phase === "BROWSE") return;
      const line = lines.find((l) => l.productId === widgetPicked.productId && l.variantId === variantId);
      if (line === undefined) return;
      if (quantity <= 0) void removeLine(line.lineId);
      else void setLineQuantity(line.lineId, quantity);
    },
    [widgetPicked, flow.phase, lines, removeLine, setLineQuantity],
  );

  /* B355 (widget) — Start over: only a CART can be walked away from
     (SHOPPING_CONTINUED); the abandoned cart's hold expires server-side.
     A PENDING order keeps pay()'s retry and the expiry arrow instead. */
  const widgetStartOver = useCallback(() => {
    if (flow.phase !== "CART") return;
    dispatch({ type: "SHOPPING_CONTINUED" });
    session.clearCartId();
    setLines([]);
    setTotal(undefined);
    setAdjustments([]);
    setCharges([]);
  }, [flow.phase, session]);

  /* B415 (a): CLEAR CART empties everything and RELEASES THE HOLD NOW.
     From PENDING_PAYMENT the order is abandoned FIRST (order.abandon —
     §5.1's expire edge on demand; SUCCEEDED intent refuses server-side);
     a refused abandon leaves the cart standing with the refusal in words.
     The authorized face never offers the door. */
  const clearCart = useCallback(async () => {
    setNotice(undefined);
    if (flow.phase === "PENDING_PAYMENT") {
      if (flow.authorized) return;
      try {
        await authed.call("order.abandon", { orderId: flow.orderId }, { clientKey: `abandon-${flow.orderId}` });
      } catch (error) {
        setNotice({
          screen: "payment",
          text: error instanceof ApiError ? translateRefusal(error) : UNKNOWN_FAILURE,
          reference: error instanceof ApiError ? error.requestId : undefined,
        });
        return;
      }
    } else if (flow.phase !== "CART") return;
    dispatch({ type: "CART_CLEARED" });
    session.clearCartId();
    setLines([]);
    setTotal(undefined);
    setAdjustments([]);
    setCharges([]);
    setPromoApplied(false);
    setPromoRefusal(undefined);
    setNameRefusal(undefined);
    setNotice(undefined);
  }, [flow, authed, session]);

  /* B545 (e) — RE-OPEN AFTER CLOSE. The embed dialog removes its frame on
     close, so every open boots fresh over the session's remembered cart. A
     cart already CHECKED OUT whose order still holds seats RESUMES at Pay
     (the strip counting, "Your tickets are still held", Start over); a
     lapsed / settled / unknown one opens a fresh cart silently — the buyer
     never reads "already on its way to payment". Once per boot, after the
     catalog (the lines speak product names). */
  const resumeChecked = useRef(false);
  const [resumed, setResumed] = useState(false);
  useEffect(() => {
    if (!dialogWalk || resumeChecked.current || catalogLoading || flow.phase !== "BROWSE") return;
    resumeChecked.current = true;
    const cartId = session.cartId();
    if (cartId === undefined) return;
    void (async () => {
      const cart = (await authed.call("cart.get", { cartId }).catch(() => undefined)) as
        | { status: "OPEN" | "CHECKED_OUT" }
        | undefined;
      const order =
        cart?.status === "CHECKED_OUT"
          ? ((
              (await authed.call("order.get", { orderId: `order-${cartId}` }).catch(() => undefined)) as
                | { order?: { orderId: string; state: string; holdExpiresAt?: number } }
                | undefined
            )?.order)
          : undefined;
      const decision = resumeDecision({ cart, order, now: Date.now() });
      if (decision.kind === "keep") return;
      if (decision.kind === "fresh") {
        session.clearCartId();
        return;
      }
      await refreshCart(cartId).catch(() => undefined);
      dispatch({ type: "CART_OPENED", cartId });
      dispatch({ type: "CHECKOUT_STARTED", proposedKey: `checkout-${cartId}` });
      dispatch({ type: "CHECKOUT_OK", orderId: decision.orderId, holdExpiresAt: decision.deadline });
      setResumed(true);
    })();
  }, [dialogWalk, catalogLoading, flow.phase, session, authed, refreshCart]);
  useEffect(() => {
    if (flow.phase !== "PENDING_PAYMENT") setResumed(false);
  }, [flow.phase]);
  /* B545 (e) — Start over: abandon the pending order (seats free NOW) and
     open the tickets on a fresh cart — CLEAR CART's own walk. */
  const dialogStartOver = useCallback(async () => {
    await clearCart();
  }, [clearCart]);

  /* §7 step 5 — the webhook confirms; the storefront polls the order. */
  useEffect(() => {
    if (flow.phase !== "PENDING_PAYMENT") return;
    const { orderId, holdExpiresAt, authorized } = flow;
    const timer = window.setInterval(() => {
      dispatch({ type: "TICK", at: Date.now() });
      /* B447: the keep-polling decision lives in lib/orderPoll.ts — the
         native twins replay the same policy (authorized outlives the hold,
         B29; unauthorized stops at the deadline). */
      if (!shouldPollOrder({ authorized, now: Date.now(), holdExpiresAt })) return;
      authed
        .call("order.get", { orderId })
        .then((res) => {
          const { order } = res as { order?: { state: string; confirmationCode?: string } };
          if (order?.state === "CONFIRMED") {
            /* B115: the code rides the same read that observes CONFIRMED. */
            setConfirmationCode(order.confirmationCode);
            dispatch({ type: "PAYMENT_CONFIRMED" });
          }
        })
        .catch(() => undefined);
    }, ORDER_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [authed, flow]);

  /* §7 step 5 — tickets: the list, then each signed token (B20b(e)). */
  useEffect(() => {
    if (flow.phase !== "CONFIRMED") return;
    const { orderId } = flow;
    /* B115: ensure the code is present even when CONFIRMED arrived without a poll. */
    setDoneCharges([]); // B629 (c): never the previous order's breakdown while this read is out
    void authed
      .call("order.get", { orderId })
      .then((res) => {
        const { order } = res as {
          order?: { confirmationCode?: string; fees?: ChargeWire["fees"]; taxTotal?: ChargeWire["taxTotal"] };
        };
        if (order?.confirmationCode !== undefined) setConfirmationCode(order.confirmationCode);
        if (order !== undefined) setDoneCharges(chargeRows(order));
      })
      .catch(() => undefined);
    (async () => {
      try {
        const res = (await authed.call("entitlement.list", { orderId })) as {
          entitlements: readonly EntitlementSummary[];
        };
        setEntitlements(res.entitlements);
        const withTokens = await Promise.all(
          res.entitlements.map(async (e) => {
            try {
              const t = (await authed.call("entitlement.token", { entitlementId: e.entitlementId })) as {
                token: string;
              };
              /* B68(b): the token renders as a QR; failure keeps the honest pending state. */
              let qrDataUrl: string | undefined;
              try {
                qrDataUrl = await ticketQrDataUrl(t.token);
              } catch {
                qrDataUrl = undefined;
              }
              return { ...e, token: t.token, ...(qrDataUrl !== undefined ? { qrDataUrl } : {}) };
            } catch {
              return e; // the email rail still has the ticket; the screen stays honest
            }
          }),
        );
        setEntitlements(withTokens);
      } catch {
        setEntitlements([]);
      }
    })();
  }, [authed, flow]);

  /* B524 — purchase follows the DOMAIN fact: the order is CONFIRMED and its
     public code is in hand. Once per order (the tracker remembers the code
     for the session), so a confirmation reload does not count twice. */
  useEffect(() => {
    if (flow.phase !== "CONFIRMED" || confirmationCode === undefined) return;
    const value = total ?? linesValue(lines, config.currency);
    tracker.purchase({
      transaction_id: confirmationCode,
      currency: value.currency,
      value: gtmAmount(value),
      ...(appliedCoupon.current !== undefined ? { coupon: appliedCoupon.current } : {}),
      ...(businessName !== undefined && businessName !== "" ? { affiliation: businessName } : {}),
      items: lineItems(lines, variantNames),
    });
  }, [flow.phase, confirmationCode, total, lines, config.currency, tracker, businessName, variantNames]);

  /* B31a — report the §9.2 outcome events to the embedding host page. */
  useEffect(() => {
    if (bridge === undefined) return;
    if (flow.phase === "CONFIRMED") bridge.sendSuccess(flow.orderId);
    /* B523 — in the dialog walk expiry returns to CART inside the sheet;
       a cancel here would close it over the buyer's kept lines. */
    else if (flow.phase === "EXPIRED" && !dialogWalk) bridge.sendCancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on phase entry
  }, [bridge, flow.phase, dialogWalk]);

  const restart = useCallback(() => {
    appliedCoupon.current = undefined;
    session.clearCartId();
    setLines([]);
    setTotal(undefined);
    setAdjustments([]);
    setCharges([]);
    setPromoApplied(false);
    setPromoRefusal(undefined);
    setDeferred(undefined);
    setGatewayNotice(undefined);
    deferredFor.current = undefined;
    resetBrowse();
    dispatch({ type: "RESTARTED" });
  }, [session, resetBrowse]);

  const applyPromo = useCallback(
    async (raw: string) => {
      /* B312 — one guard, one channel. The view no longer decides anything;
         an empty box is refused HERE, out loud, with the same sentence slot
         a turned-down code uses. */
      const attempt = promoAttempt(raw);
      if (attempt.kind === "refuse") {
        setPromoRefusal(attempt.why);
        return;
      }
      const cartId = session.cartId();
      if (cartId === undefined) return;
      const { code } = attempt;
      /* B338 — the same door treatment, and deliberately NOT the same
         argument: `applyPromoCode` is idempotent by construction, so a
         second press is harmless on the wire. This door was faceless and
         pressable, which is an announcement-and-door defect, and it is cured
         beside Remove because they are one screen's one silence — not
         because they carry one risk. */
      if (!beginCartAct("cart.apply_promo", cartId)) return;
      setPromoRefusal(undefined);
      setNotice(undefined);
      try {
        const res = (await authed.call("cart.apply_promo", { cartId, code })) as {
          applied: boolean;
          totals: { total: Money; adjustments: readonly CartAdjustment[]; fees?: readonly { feeId: string; label: string; amount: Money }[]; taxTotal?: Money };
        };
        setPromoApplied(res.applied);
        if (res.applied) appliedCoupon.current = code;
        /* The seller's answer was no — say so. Silence here is what made a
           rejection indistinguishable from a press that never landed. */
        setPromoRefusal(res.applied ? undefined : promoRefusedWhy(code));
        setTotal(res.totals.total);
        setAdjustments(res.totals.adjustments);
        setCharges(chargeRows(res.totals));
      } catch (error) {
        report("cart", error);
      } finally {
        endCartAct();
      }
    },
    [authed, beginCartAct, endCartAct, session, report],
  );

  const pickedCard = picked === undefined ? undefined : products.find((p) => p.productId === picked.productId);

  /* B57 — which card the journey detail is walking right now. */
  const journeyReturnStage = browseStage === "returnWhen" || browseStage === "returnCompose";
  const journeyActiveCard = trip === undefined ? undefined : journeyReturnStage ? trip.ret : trip.outbound;
  const journeyActive = journeyActiveCard === undefined ? undefined : journeyOf(journeyActiveCard);

  const screen = pickScreen(flow);
  /* B332 — the sentence, if this is still the step it is about. Everything
     downstream asks THIS, never the raw state: `showNotice` would otherwise
     be true for a refusal the buyer can no longer see. */
  const refusal = noticeOnScreen(notice, screen);
  const refusalReference = noticeReferenceOnScreen(notice, screen);
  /* B169(a) — ONE face. An unconnected page has no §7 walk to be part of:
     no progress, no notice, no catalog. */
  /* B181 — while the siteKey resolve is in flight, the page is LOADING,
     not unconnected: the teaching face only lands once the miss is real. */
  const face = resolvingSiteKey
    ? ({ face: "loading", showNotice: false } as const)
    : bootFace({ boot, catalogLoading, ...(refusal !== undefined ? { notice: refusal } : {}) });
  const unconnected = face.face === "unconnected" && boot.kind === "unconnected";
  /* B397 (75:2) — the booking page's live cart count and its one door. */
  const cartCount = lines.reduce((n, l) => n + l.quantity, 0);
  const openCartDoor = (): void => {
    const cartId = session.cartId();
    if (cartId !== undefined) dispatch({ type: "CART_OPENED", cartId });
  };
  const heroNote = accompanimentNote(products, vocab);
  /* B517 — the chrome this face wears, by where it lives (lib/presentation.ts). */
  const chrome = embedChrome(ctx.distribution, ctx.presentation);
  /* B523 — where the sheet's walk stands, from the flow machine alone. */
  const dialogStep: DialogStep =
    flow.phase === "CONFIRMED"
      ? "done"
      : flow.phase === "PENDING_PAYMENT"
        ? "pay"
        : flow.phase === "CART" || flow.phase === "CHECKOUT_IN_FLIGHT"
          ? "cart"
          : "tickets";
  const dialogHoldMs = holdMsLeft(flow, now);
  /* Back from CART re-opens the tickets with the cart kept (a legal arrow). */
  const dialogBackToTickets = (): void => {
    if (flow.phase === "CART") dispatch({ type: "SHOPPING_CONTINUED" });
  };
  /* B549 leg (2) — the interrupt's doors. The form opens knowing the party's
     count and day; the clientKey is minted HERE so a double-pressed SEND is
     one inquiry (Inv 4). "keep 19" steps the party back under the threshold
     and the calendar column returns (the frame's own arrow). */
  /* B580 — a party is a group at the threshold OR one past a type's own cap (groupParty). */
  const groupNow = pickedCard === undefined ? undefined : groupParty(pickedCard, comp, groupThreshold);
  const groupInterrupt = groupStage === undefined && screen === "browse" && groupNow !== undefined;
  const openGroupInquiry = (): void => {
    setGroupDraft(seedInquiryDraft(compositionTotal(comp), dayKey));
    setGroupKey(crypto.randomUUID());
    setGroupError(undefined);
    setGroupStage("form");
  };
  const keepTickets = (): void => {
    if (groupThreshold !== undefined) setComp((prev) => reduceToKeep(prev, groupThreshold, pickedCard));
  };
  const sendGroupInquiry = async (): Promise<void> => {
    if (groupDraft === undefined || groupKey === undefined || !inquiryReady(groupDraft)) return;
    setGroupBusy(true);
    setGroupError(undefined);
    try {
      await authed.call("group.inquiry", inquiryBody(groupDraft, config.channelId), { clientKey: groupKey });
      setGroupStage("sent");
    } catch (error) {
      setGroupError(inquirySendRefusal(error));
    } finally {
      setGroupBusy(false);
    }
  };
  const listed =
    ctx.presentation === "list" && ctx.preselect.productId !== undefined && products.some((p) => p.productId === ctx.preselect.productId)
      ? products.filter((p) => p.productId === ctx.preselect.productId)
      : products;
  /* B653 (c) — the foot's left line: the name, then where guests find the venue (553:2). */
  const footLine = footFacts(businessName, venue);
  const page = (
    <main
      className={`storefront ${ctx.distribution}${ctx.presentation !== undefined ? ` embed-${ctx.presentation}` : ""}${widgetMode && !unconnected ? " widget" : screen === "browse" && !unconnected ? " booking" : mergedPay && !unconnected ? " sheet" : ""}`}
    >
      {/* B517 — the dialog's own close door: the sheet is ours, so is the
          way out (the host adds Esc on its page and a backdrop press). */}
      {chrome.closeDoor && bridge !== undefined ? (
        <p className="embed-close">
          <Button variant="ghost" ariaLabel="Close" onClick={() => bridge.sendClose()}>
            <Icon name="close" size={16} />
          </Button>
        </p>
      ) : null}
      {/* B523 — the sheet's chrome, drawn by the storefront so it wears the
          tenant theme: header + trail + hold strip pinned over the walk. */}
      {chrome.sheet && !unconnected ? (
        <DialogShell
          step={dialogStep}
          {...(groupStage !== undefined ? { groupStage, redlinePage: "embed-group-inquiry" } : groupInterrupt && dialogStep === "tickets" ? { redlinePage: "embed-group-interrupt" } : {})}
          onClose={() => bridge?.sendClose()}
          {...(groupStage === "form"
            ? { onBack: () => setGroupStage(undefined) }
            : groupStage === "sent"
              ? {}
              : dialogStep === "tickets" && picked !== undefined
                ? { onBack: resetBrowse }
                : {})}
          {...(groupStage === undefined && dialogStep === "cart" ? { onBack: dialogBackToTickets, onVisit: () => dialogBackToTickets() } : {})}
          {...(dialogHoldMs !== undefined ? { holdMsLeft: dialogHoldMs } : {})}
        />
      ) : null}
      {chrome.sheet ? null : widgetMode && !unconnected ? null : screen === "browse" && !unconnected && picked !== undefined && trip === undefined ? (
        /* B397 leg (2) — the tickets step's own head (72:2): channel tile +
           the page's name. No nav, no cart chip — the sticky bar is this
           page's one door forward. The tenant NAME the frame draws stays
           absent (no read carries one — the priced B355 amendment). */
        <TicketsPageHead
          pageName="Tickets"
          redline="tickets-head"
          {...(brandLogoUrl !== undefined ? { logoUrl: brandLogoUrl } : {})}
          {...(businessName !== undefined ? { brandName: businessName } : {})}
        >
          {ctx.distribution === "embedded" ? null : <ThemeToggle />}
        </TicketsPageHead>
      ) : screen === "browse" && !unconnected ? (
        /* B397 — the browse walk wears the 75:2 shell head; the flow screens
           keep the stepper header until their own legs (2)–(4) land. */
        chrome.head ? (
          <BookingHead
            {...(chrome.cartChip ? { cartCount, onOpenCart: openCartDoor } : {})}
            {...(brandLogoUrl !== undefined ? { logoUrl: brandLogoUrl } : {})}
            {...(businessName !== undefined ? { brandName: businessName } : {})}
          >
            {/* B72(b): hosted pages get the switcher; embedded defers to the host's theme hints. */}
            {ctx.distribution === "embedded" ? null : <ThemeToggle />}
          </BookingHead>
        ) : null
      ) : mergedPay && !unconnected ? (
        /* B397 leg (3) — 73:2: canvas/head identical to the tickets step. */
        <TicketsPageHead
          pageName="Pay"
          redline="cartpay-head"
          {...(brandLogoUrl !== undefined ? { logoUrl: brandLogoUrl } : {})}
          {...(businessName !== undefined ? { brandName: businessName } : {})}
        >
          {ctx.distribution === "embedded" ? null : <ThemeToggle />}
        </TicketsPageHead>
      ) : (
        <header className="shell-header">
          {brandLogoUrl !== undefined ? <img className="brand-logo" src={brandLogoUrl} alt="" /> : null}
          {/* B403 (74:2) — the tenant's name rides every shell head now that
              the mint carries it; absence stays the honest face. */}
          {businessName !== undefined ? <span className="booking-brand-name booking-brand-name--sm">{businessName}</span> : null}
          {unconnected ? null : <Stepper current={screen} />}
          {/* B72(b): hosted pages get the switcher; embedded defers to the host's theme hints. */}
          {ctx.distribution === "embedded" ? null : <ThemeToggle />}
        </header>
      )}
      {unconnected && boot.kind === "unconnected" ? <NotConnectedView reason={boot.reason} /> : null}
      {unconnected ? null : (
        <>
      {face.showNotice && refusal !== undefined ? <Notice kind="error" reference={refusalReference}>{refusal}</Notice> : null}
      {/* B403 #23 (a) — §10: a preselect that names no live product says why
          the page shows everything; a wrong snippet must not read as a whim. */}
      {preselectGone !== undefined ? (
        <Notice kind="info">
          This page was asked to open a product that isn&#39;t on sale right now — showing everything that is.
        </Notice>
      ) : null}
      {widgetMode && widgetPicked !== undefined ? (
        /* B355 (58:238) — the ONE fused card; the host page owns everything
           around it. Loading keeps the quiet busy face, not a skeleton page. */
        slotsLoading && flow.phase === "BROWSE" ? (
          <Busy label="Loading times…" />
        ) : (
          <WidgetView
            card={widgetPicked}
            slots={slots}
            availability={availability}
            {...whenOpts}
            comp={comp}
            {...(vocab === undefined ? {} : { vocab })}
            onChange={widgetChange}
            {...(chosenSlot !== undefined ? { chosenSlotId: chosenSlot.slotId } : {})}
            onPickSlot={pickDeparture}
            {...(dayKey !== undefined ? { selectedDayKey: dayKey } : {})}
            onSelectDay={setDayKey}
            {...(quoteTotal !== undefined ? { total: quoteTotal } : {})}
            buyerName={buyerName}
            onBuyerName={(value) => {
              setBuyerName(value);
              setNameRefusal(undefined);
            }}
            nameRefusal={nameRefusal}
            buyerEmail={buyerEmail}
            onBuyerEmail={(value) => {
              setBuyerEmail(value);
              setEmailRefusal(undefined);
            }}
            emailRefusal={emailRefusal}
            acknowledgement={acknowledgement}
            acknowledged={ackTicked}
            onAcknowledged={(ticked) => {
              setAckTicked(ticked);
              setAcknowledgeRefusal(undefined);
            }}
            acknowledgeRefusal={acknowledgeRefusal}
            optIn={optIn}
            optedIn={optedIn}
            onOptIn={setOptedIn}
            wallets={wallets}
            gatewayReady={deferred !== undefined}
            {...(gatewayNotice !== undefined ? { gatewayNotice } : {})}
            authorized={flow.phase === "PENDING_PAYMENT" && flow.authorized}
            paying={paying || screen === "checkout_in_flight"}
            onPay={() => void widgetPay()}
            testMode={publishableKey !== undefined && publishableKey.startsWith("pk_test")}
            {...(dialogHoldMs !== undefined ? { holdMsLeft: dialogHoldMs } : {})}
            locked={flow.phase !== "BROWSE"}
            {...(flow.phase === "CART" ? { onStartOver: widgetStartOver } : {})}
            {...(ctx.preselect.productId === undefined ? { onBack: resetBrowse } : {})}
          />
        )
      ) : null}
      {!widgetMode && screen === "browse" ? (
        <div className="booking-content">
          {picked === undefined || pickedCard === undefined ? (
            face.face === "loading" ? (
              <Busy label="Loading tickets…" />
            ) : (
              <>
                {/* B397/B403 #28 (75:2): hero, then the grid — the cards are
                    the page's doors, ROUTES INCLUDED. The planner is retired:
                    the frame draws one card per product, and a route card
                    starts its own journey walk. */}
                {showFace ? (
                  <PosterWall
                    products={listed}
                    feeds={posterFeeds}
                    zone={whenOpts}
                    {...(businessName !== undefined && businessName !== "" ? { clubName: businessName } : {})}
                    now={posterNow.current}
                    loading={posterLoading}
                    onOpen={(id) => pickProduct(id)}
                    onShowtime={(id, slotId, start) => {
                      pendingShowtime.current = { productId: id, slotId, start };
                      pickProduct(id);
                    }}
                    onNight={(id, slotId, start) => {
                      pendingShowtime.current = { productId: id, slotId, start, nightOnly: true };
                      pickProduct(id);
                    }}
                    view={showView}
                    onView={setShowView}
                    month={showMonth}
                    onMonth={setShowMonth}
                  />
                ) : null}
                {!showFace && chrome.hero ? (
                  <BookingHero
                    title={headline ?? catalogSummary(products, vocab)}
                    {...(heroNote !== undefined ? { note: heroNote } : {})}
                  />
                ) : null}
                {showFace ? null : <ProductList
                  products={listed}
                  priceFacts={priceFacts}
                  nextFacts={nextFacts}
                  vocab={vocab}
                  /* B517 — in the inline list a press asks the HOST for the
                     booking dialog; everywhere else it walks in place. */
                  onPick={(id) => {
                    /* B524 — select_item is the LIST press, wherever it walks. */
                    const index = products.findIndex((p) => p.productId === id);
                    if (index >= 0) {
                      const item = listItems(products, config.channelId)[index];
                      if (item !== undefined) tracker.track("select_item", { item_list_id: config.channelId, items: [item] });
                    }
                    if (chrome.pressOpensDialog && bridge !== undefined) bridge.sendOpen(id);
                    else pickProduct(id);
                  }}
                />}
              </>
            )
          ) : (
            <section className="product-detail">
              <div className="back">
                {/* the way back the frame does not draw but FORMS R14 demands — a
                    trapped buyer is worse than one extra ghost door */}
                <Button variant="ghost" onClick={resetBrowse}>
                  {/* B614 (555:2): a comedy club's way back names its listing */}
                  {showFace ? "‹ All shows" : "‹ All tickets"}
                </Button>
              </div>
              {trip !== undefined ? <h2>{journeyActiveCard?.name ?? ""}</h2> : null}
              {trip?.ret !== undefined ? (
                <p className="direction" role="status">
                  {journeyReturnStage ? "Return sailing" : "Outbound sailing"}
                </p>
              ) : null}
              {adding ? (
                <Busy label="Adding to your cart…" />
              ) : trip !== undefined && journeyActiveCard !== undefined ? (
                browseStage === "compose" || browseStage === "returnCompose" ? (
                  /* B57 — per-direction party composer (return prefilled from outbound). */
                  <CompositionView
                    card={journeyActiveCard}
                    comp={browseStage === "returnCompose" ? retComp : comp}
                    onChange={(variantId, qty) =>
                      browseStage === "returnCompose"
                        ? setRetComp((prev) => setQuantity(journeyActiveCard, prev, variantId, qty))
                        : setComp((prev) => setQuantity(journeyActiveCard, prev, variantId, qty))
                    }
                    onAdd={completeComposition}
                    {...(browseStage === "returnCompose"
                      ? retSlot?.label !== undefined
                        ? { slotLabel: retSlot.label }
                        : {}
                      : chosenSlot?.label !== undefined
                        ? { slotLabel: chosenSlot.label }
                        : {})}
                    {...(browseStage === "compose" && trip.ret !== undefined ? { addLabel: returnPickLabel(vocab) } : {})}
                  />
                ) : slotsLoading ? (
                  <Busy label="Finding times…" />
                ) : (
                  <>
                    {/* B403 #28 — the trip-type pill rides the OUTBOUND departures
                        step, only where the catalog honestly sells the reverse
                        O-D (the planner's one surviving control). */}
                    {browseStage === "when" && returnCardFor(groups, trip.outbound) !== undefined ? (
                      <TripTypeChoice roundTrip={trip.ret !== undefined} onChange={chooseTripType} />
                    ) : null}
                    {/* B57 — departures narrowed to the journey's BOARDING leg. */}
                    <DeparturePicker
                    {...whenOpts}
                    slots={journeyActive === undefined ? slots : journeyDepartures(slots, journeyActive)}
                    availability={availability}
                    fits={fits}
                    onPick={pickDeparture}
                    onBrowseAll={resetBrowse}
                    {...(calPrices !== undefined && calPrices.productId === (journeyActiveCard ?? pickedCard)?.productId
                      ? { pricing: calPrices.pricing }
                      : {})}
                    {...(dayKey !== undefined ? { selectedDayKey: dayKey } : {})}
                      onSelectDay={setDayKey}
                    />
                  </>
                )
              ) : slotsLoading ? (
                <Busy label="Finding times…" />
              ) : (
                /* B397 leg (2) — ONE card (72:2): day, departure and counts at
                   once, CONTINUE with the total riding the sticky bar. The
                   exact-fit calendar's slot.fit verdicts stay on the journey
                   walk; here the cart's own wall is the wall (noted on the
                   item line). */
                groupStage !== undefined && groupDraft !== undefined ? (
                  /* B549 leg (2) — the inquiry walk replaces the buying walk
                     (446:2: the trail says so). Sent keeps the sheet open on
                     its promise; Done hands the sheet back to the host. */
                  <div className="group-walk">
                    {groupStage === "sent" ? (
                      <GroupInquirySent
                        email={groupDraft.contactEmail}
                        deskName={groupsContactName}
                        onClose={chrome.sheet ? () => bridge?.sendClose() : () => setGroupStage(undefined)}
                      />
                    ) : (
                      <GroupInquiryForm
                        draft={groupDraft}
                        onChange={(next) => {
                          setGroupDraft(next);
                          /* the buyer's own edit answers a pinned refusal; a sentence-only refusal stays until the next send */
                          if (groupError !== undefined && Object.keys(groupError.fields).length > 0) setGroupError(groupError.text === undefined ? undefined : { ...groupError, fields: {} });
                        }}
                        dayOptions={groupDepartures(slots).map((g) => ({ value: g.key, label: g.label }))}
                        threshold={groupNow?.size ?? groupThreshold ?? 20}
                        deskName={groupsContactName}
                        experience={{
                          name: pickedCard.name,
                          photoUrl: pickedCard.presentation?.photos?.[0]?.url,
                          publicFrom: (() => {
                            const priced = pickedCard.variants.filter((v) => v.displayPrice !== undefined);
                            if (priced.length === 0) return undefined;
                            const low = priced.reduce((a, b) => ((a.displayPrice?.amount ?? 0) <= (b.displayPrice?.amount ?? 0) ? a : b));
                            return low.displayPrice === undefined ? undefined : formatMoney(low.displayPrice);
                          })(),
                        }}
                        error={groupError?.text}
                        errorReference={groupError?.reference}
                        refused={groupError?.fields}
                      />
                    )}
                    {groupStage === "form" ? (
                      <>
                        {/* B589 — the shut reason is drawn VISIBLY by the form itself (id gi-gate). */}
                        {chrome.sheet ? (
                          <SheetFoot
                            pinned
                            redline="embed-group-inquiry-footer"
                            totalRedline="embed-group-inquiry-footer-title"
                            subRedline="embed-group-inquiry-footer-line"
                            total="No payment now"
                            sub={deskRepliesWords(groupsContactName)}
                          >
                            <Button
                              size="lg"
                              type="submit"
                              form={GROUP_INQUIRY_FORM_ID}
                              disabled={inquiryGateSentence(groupDraft) !== undefined}
                              busy={groupBusy}
                              {...(inquiryGateSentence(groupDraft) !== undefined ? { describedBy: "gi-gate" } : {})}
                              onClick={() => void sendGroupInquiry()}
                              data={{ redline: "embed-group-inquiry-cta" }}
                            >
                              <span data-redline="embed-group-inquiry-cta-text">SEND INQUIRY</span>
                            </Button>
                          </SheetFoot>
                        ) : (
                          <StickyBar redline="group-inquiry-sticky" seatedWhenWide summary={`No payment now — ${deskRepliesLead(groupsContactName).replace(/^The /, "the ")} within one business day with a quote`}>
                            <Button
                              size="lg"
                              type="submit"
                              form={GROUP_INQUIRY_FORM_ID}
                              disabled={inquiryGateSentence(groupDraft) !== undefined}
                              busy={groupBusy}
                              {...(inquiryGateSentence(groupDraft) !== undefined ? { describedBy: "gi-gate" } : {})}
                              onClick={() => void sendGroupInquiry()}
                            >
                              Send inquiry
                            </Button>
                          </StickyBar>
                        )}
                      </>
                    ) : null}
                  </div>
                ) : (
                <>
                  {chrome.sheet ? null : (
                  <FlowStepper
                    steps={[
                      { key: "tickets", label: "Tickets" },
                      { key: "cart", label: "Cart" },
                      { key: "pay", label: "Pay" },
                      { key: "done", label: "Done" },
                    ]}
                    activeKey="tickets"
                    redline="tickets-flow"
                  />
                  )}
                  {showFace && !chrome.sheet ? (
                    /* B614 (b) — the SHOW face's tickets step (555:2): nights as
                       chips, the headliner's bill, the policy facts. */
                    <ShowTicketsStep
                      {...whenOpts}
                      card={pickedCard}
                      slots={slots}
                      availability={availability}
                      comp={comp}
                      onChange={(variantId, qty) => setComp((prev) => setQuantity(pickedCard, prev, variantId, qty, groupThreshold))}
                      {...(chosenSlot !== undefined ? { chosenSlotId: chosenSlot.slotId } : {})}
                      onPickSlot={pickDeparture}
                      {...(dayKey !== undefined ? { selectedDayKey: dayKey } : {})}
                      onSelectDay={setDayKey}
                      onContinue={completeComposition}
                      now={posterNow.current}
                      {...(productMedia[pickedCard.productId] !== undefined ? { media: productMedia[pickedCard.productId] } : {})}
                      {...(groupThreshold !== undefined ? { groupThreshold } : {})}
                      {...(businessName !== undefined ? { businessName } : {})}
                      onRequestGroupQuote={openGroupInquiry}
                    />
                  ) : (
                  <TicketsStep
                    {...(chrome.sheet ? { foot: "sheet" as const } : {})}
                    {...whenOpts}
                    card={pickedCard}
                    slots={slots}
                    availability={availability}
                    comp={comp}
                    onChange={(variantId, qty) => setComp((prev) => setQuantity(pickedCard, prev, variantId, qty, groupThreshold))}
                    {...(chosenSlot !== undefined ? { chosenSlotId: chosenSlot.slotId } : {})}
                    onPickSlot={pickDeparture}
                    {...(dayKey !== undefined ? { selectedDayKey: dayKey } : {})}
                    onSelectDay={setDayKey}
                    onContinue={completeComposition}
                    onBrowseAll={resetBrowse}
                    {...(vocab === undefined ? {} : { vocab })}
                    {...(productMedia[pickedCard.productId] !== undefined ? { media: productMedia[pickedCard.productId] } : {})}
                    {...(groupThreshold !== undefined ? { groupThreshold } : {})}
                    {...(businessName !== undefined ? { businessName } : {})}
                    onRequestGroupQuote={openGroupInquiry}
                    onKeepTickets={keepTickets}
                  />
                  )}
                </>
                )
              )}
            </section>
          )}
        </div>
      ) : null}
      {mergedPay && !unconnected && !widgetMode ? (
        /* B397 leg (3) — ONE page (73:2): the cart card, the pay card and the
           sticky PAY. It stays mounted across CART → CHECKOUT_IN_FLIGHT →
           PENDING_PAYMENT so the provider's element survives the LATE chain. */
        <div className="sheet-content">
          {chrome.sheet ? null : (
          <FlowStepper
            steps={[
              { key: "tickets", label: "Tickets" },
              { key: "cart", label: "Cart" },
              { key: "pay", label: "Pay" },
              { key: "done", label: "Done" },
            ]}
            activeKey="pay"
            redline="cartpay-flow"
          />
          )}
          <CartPayView
            {...(chrome.sheet
              ? {
                  step: dialogStep === "pay" ? ("pay" as const) : ("cart" as const),
                  onCheckout: () => void dialogContinue(),
                  checking: dialogStep === "cart" && !cartAvailChecked,
                  onEditLine: dialogEditLine,
                  resumed,
                  ...(dialogHoldMs !== undefined ? { resumedMsLeft: dialogHoldMs } : {}),
                  onStartOver: () => void dialogStartOver(),
                }
              : {})}
            {...(showFace
              ? {
                  showHeads: cartShowHeads(lines, products, {
                    ...(businessName !== undefined && businessName !== "" ? { clubName: businessName } : {}),
                    ageOf: (slotId) => slots.find((sl) => sl.slotId === slotId)?.minAge,
                  }),
                }
              : {})}
            lines={cartCardLines(lines, products, (l) =>
              l.slotId !== undefined && slotLabels[l.slotId] !== undefined
                ? slotLabels[l.slotId]
                : l.slotStart !== undefined
                  ? slotDisplay(l.slotStart, whenOpts)
                  : undefined,
            )}
            total={total}
            adjustments={adjustments}
            charges={charges}
            promoApplied={promoApplied}
            promoRefusal={promoRefusal}
            onApplyPromo={(code) => void applyPromo(code)}
            {...(!chrome.sheet && dialogHoldMs !== undefined ? { holdMsLeft: dialogHoldMs } : {})}
            buyerName={buyerName}
            onBuyerName={(value) => {
              setBuyerName(value);
              setNameRefusal(undefined);
            }}
            nameRefusal={nameRefusal}
            buyerEmail={buyerEmail}
            onBuyerEmail={(value) => {
              setBuyerEmail(value);
              setEmailRefusal(undefined);
            }}
            emailRefusal={emailRefusal}
            wallets={wallets}
            buyerPhone={buyerPhone}
            onBuyerPhone={(value) => {
              setBuyerPhone(value);
              setPhoneRefusal(undefined);
            }}
            phoneRefusal={phoneRefusal}
            acknowledgement={acknowledgement}
            acknowledged={ackTicked}
            onAcknowledged={(ticked) => {
              setAckTicked(ticked);
              setAcknowledgeRefusal(undefined);
            }}
            acknowledgeRefusal={acknowledgeRefusal}
            optIn={optIn}
            optedIn={optedIn}
            onOptIn={setOptedIn}
            {...(flow.phase === "CART" || (flow.phase === "PENDING_PAYMENT" && !flow.authorized)
              ? { onClearCart: () => void clearCart() }
              : {})}
            gatewayReady={deferred !== undefined}
            {...(gatewayNotice !== undefined ? { gatewayNotice } : {})}
            testMode={publishableKey !== undefined && publishableKey.startsWith("pk_test")}
            authorized={flow.phase === "PENDING_PAYMENT" && flow.authorized}
            paying={paying || screen === "checkout_in_flight"}
            hints={chrome.sheet && dialogStep === "cart" ? [...cartHints, ...dialogAvailHints] : cartHints}
            onPay={() => void pay()}
            onSetQuantity={(lineId, quantity) => void setLineQuantity(lineId, quantity)}
            onRemove={(lineId) => void removeLine(lineId)}
            {...(session.cartId() !== undefined ? { cartId: session.cartId() as string } : {})}
            cartBusy={cartBusy}
            {...(flow.phase === "CART"
              ? {
                  onContinue: () => {
                    resetBrowse();
                    dispatch({ type: "SHOPPING_CONTINUED" });
                  },
                }
              : {})}
          />
        </div>
      ) : null}
      {screen === "confirmed" && flow.phase === "CONFIRMED" ? (
        /* B397 leg (4) — the buyer's ticket home (74:2): the flow rail with
           DONE active over the Done card, the tickets card and the sticky
           save door; the page rides the same sheet column as the other 390
           steps. */
        <div className="sheet-content">
          {chrome.sheet ? null : (
          <FlowStepper
            steps={[
              { key: "tickets", label: "Tickets" },
              { key: "cart", label: "Cart" },
              { key: "pay", label: "Pay" },
              { key: "done", label: "Done" },
            ]}
            activeKey="done"
            redline="confirm-flow"
          />
          )}
          <ConfirmationView
            orderId={flow.orderId}
            tickets={entitlements}
            {...(confirmationCode !== undefined ? { confirmationCode } : {})}
            {...(() => {
              const sentence = confirmationSummary(lines, products, slotLabels, whenOpts);
              return sentence !== undefined ? { summarySentence: sentence } : {};
            })()}
            {...(total !== undefined ? { total } : {})}
            charges={doneCharges}
            promoApplied={promoApplied}
            {...(buyerEmail.trim() !== "" ? { buyerEmail: buyerEmail.trim() } : {})}
            onSave={() => window.print()}
          />
        </div>
      ) : null}
      {screen === "expired" ? <ExpiredView onRestart={restart} /> : null}
        </>
      )}
      {/* B616 leg 3b — the club's signup line above the foot, a SHOW tenant's pages only. */}
      {widgetMode || !chrome.siteFoot || !insiderFor(tenantType) ? null : (
        <InsiderBand
          email={insiderEmail}
          onEmail={(value) => {
            setInsiderEmail(value);
            setInsiderError(undefined);
          }}
          onJoin={() => void joinInsider()}
          phase={insiderPhase}
          error={insiderError}
        />
      )}
      {/* the page foot (75:2) — pages only; a sheet's frames end at its sticky foot (chrome table). */}
      {widgetMode || !chrome.siteFoot ? null : <ShellFoot {...(footLine !== undefined ? { facts: footLine } : {})} />}
    </main>
  );
  /* B352/B397 — the canvas is the package's ONE AppCanvas; embedded frames
     defer the ground to the host page, which owns its embed (B72(b)). */
  return ctx.distribution === "hosted" ? <AppCanvas>{page}</AppCanvas> : page;
}
